import { inspectAndBenchmark } from "./src/gpu-benchmark.js";
import { probeFluxRuntime, runCanonicalRender } from "./src/flux-adapter.js";
import { deleteModelPack, inspectModelPack, installModelPack } from "./src/model-pack.js";
import { createReceipt, formatBytes, serializeReceipt } from "./src/report.js";
import { classifyRenderFailure, createAlphaEvidence } from "./src/alpha-evidence.js";
import { getContributionEligibility } from "./src/contribution-eligibility.js";
import { getExpiredEscrowPresentation } from "./src/job-presentation.js";
import { getWorkerJobPresentation } from "./src/worker-job-presentation.js";
import { getUserFacingError } from "./src/error-presentation.js";
import { chooseAutoWorkerJob, getAutoWorkerDelay } from "./src/auto-worker.js";
import {
  authorizeWorkerSession,
  revokeWorkerSession,
  signWorkerSessionPayload
} from "./src/session-browser-client.js";
import {
  createWalletDiscovery,
  friendlyWalletError,
  loadProviderPreference,
  saveProviderPreference,
  selectPreferredProvider
} from "./src/wallet-provider.js";
import modelCatalog from "./models/flux2-klein-freegen.artifacts.json";

const elements = Object.fromEntries([
  "run-button", "run-state", "verdict-light", "verdict-title", "verdict-detail",
  "metric-gflops", "metric-storage", "metric-f16", "metric-runtime", "adapter-name",
  "browser-name", "model-status", "render-step-detail", "event-log", "receipt-id",
  "copy-button", "export-button", "reset-button", "network-mode", "participant-id",
  "credit-balance", "faucet-button", "create-job-form", "create-job-button", "prompt-input",
  "reward-input", "job-form-message", "queue-gate", "refresh-queue-button", "queue-count", "job-queue",
  "model-pack-size", "model-pack-progress", "model-pack-detail", "model-pack-button",
  "canonical-render-button", "canonical-output", "canonical-image", "canonical-caption", "wallet-button",
  "wallet-control", "wallet-status", "wallet-provider-dialog", "wallet-provider-list", "wallet-provider-close",
  "refresh-results-button", "consumer-results", "worker-status", "abort-operation-button",
  "refresh-worker-history-button", "worker-history", "worker-history-count", "worker-reward-bar",
  "worker-reward-label", "worker-withdraw-button",
  "worker-bond-rail", "worker-bond-title", "worker-bond-button", "worker-bond-meter-fill",
  "worker-bond-staked", "worker-bond-available", "worker-bond-locked", "worker-bond-audit-rate",
  "worker-bond-detail",
  "auto-worker-console", "auto-worker-button", "auto-worker-detail", "auto-worker-submitted",
  "auto-worker-accepted", "auto-worker-earned", "auto-worker-last-scan", "auto-worker-note", "auto-worker-state",
  "session-key-button",
  "delete-model-button", "alpha-evidence", "alpha-submit-button", "alpha-message",
  "alpha-run-count", "alpha-completion", "alpha-device-count", "alpha-refresh-button",
  "alpha-invite-row", "alpha-invite-input", "alpha-paid-count", "admin-session-chip",
  "admin-access-state", "admin-open-jobs", "admin-accepted-jobs", "admin-pending-settlement",
  "admin-verifier-health", "admin-job-form", "admin-prompt-input", "admin-reward-input",
  "admin-create-job-button", "admin-form-message", "admin-refresh-button", "admin-job-list",
  "verifier-access-state", "verifier-refresh-button", "verifier-next-action", "verifier-registry-address",
  "verifier-phase-track", "verifier-status-chip", "verifier-staked", "verifier-available",
  "verifier-vote-bond", "verifier-rewards", "verifier-stake-form", "verifier-metadata-input",
  "verifier-stake-input", "verifier-stake-symbol", "verifier-stake-button", "verifier-unstake-button",
  "verifier-cancel-unstake-button", "verifier-withdraw-stake-button", "verifier-withdraw-rewards-button",
  "verifier-form-message", "verifier-job-count", "verifier-job-list"
].map((id) => [id, document.getElementById(id)]));

let receipt = null;
let events = [];
let benchmarkResult = null;
let currentModelProbe = null;
let canonicalImageUrl = null;
let participantId = getParticipantId();
let networkConfig = null;
let activeWorkerJobId = null;
let contributorQualification = null;
let walletSession = null;
let walletProvider = null;
let walletProviderEntry = null;
let walletConnectionInFlight = false;
let adminAuthorized = false;
let activeOperationController = null;
let autoWorkerRequested = false;
let autoWorkerLockHeld = false;
let autoWorkerLockRelease = null;
let autoWorkerTimer = null;
let autoWorkerTickInFlight = false;
let autoWorkerSubmittedCount = 0;
let autoWorkerAcceptedCount = 0;
let autoWorkerEarnedLabel = "0";
let autoWorkerConsecutiveFailures = 0;
let autoWorkerLastScanAt = null;
let autoWorkerState = "idle";
let autoWorkerDetail = "Connect, install the model, and qualify this device before starting.";
let autoWorkerLastBalanceRefreshAt = 0;
let autoWorkerWakeLock = null;
let workerSession = null;
let workerSessionInFlight = false;
let workerBondStatus = null;
let verifierStatus = null;
const autoWorkerSessionJobIds = new Set();
const resultObjectUrls = new Map();
const boundWalletProviders = new WeakSet();
const walletDiscovery = createWalletDiscovery(globalThis);
walletDiscovery.request();

elements["browser-name"].textContent = detectBrowser();
elements["run-button"].addEventListener("click", runCheck);
elements["copy-button"].addEventListener("click", copyReceipt);
elements["export-button"].addEventListener("click", exportReceipt);
elements["reset-button"].addEventListener("click", resetView);
elements["faucet-button"].addEventListener("click", requestTestCredits);
elements["create-job-form"].addEventListener("submit", createRenderJob);
elements["refresh-queue-button"].addEventListener("click", refreshQueue);
elements["model-pack-button"].addEventListener("click", downloadModelPack);
elements["canonical-render-button"].addEventListener("click", renderCanonicalWorkload);
elements["wallet-button"].addEventListener("click", connectWallet);
elements["wallet-provider-close"].addEventListener("click", () => elements["wallet-provider-dialog"].close("cancel"));
elements["refresh-results-button"].addEventListener("click", refreshConsumerResults);
elements["refresh-worker-history-button"].addEventListener("click", refreshWorkerHistory);
elements["worker-withdraw-button"].addEventListener("click", withdrawWorkerRewards);
elements["worker-bond-button"].addEventListener("click", manageWorkerBond);
elements["auto-worker-button"].addEventListener("click", toggleAutoWorker);
elements["session-key-button"].addEventListener("click", toggleWorkerSession);
elements["abort-operation-button"].addEventListener("click", abortActiveOperation);
elements["delete-model-button"].addEventListener("click", removeModelPack);
elements["alpha-submit-button"].addEventListener("click", submitAlphaEvidence);
elements["alpha-refresh-button"].addEventListener("click", refreshAlphaReport);
elements["alpha-invite-input"].addEventListener("input", updateAlphaEvidencePanel);
elements["admin-job-form"].addEventListener("submit", createGlobalJob);
elements["admin-refresh-button"].addEventListener("click", refreshAdminAccess);
elements["verifier-stake-form"].addEventListener("submit", manageVerifierStake);
elements["verifier-refresh-button"].addEventListener("click", refreshVerifierStatus);
elements["verifier-unstake-button"].addEventListener("click", requestVerifierStakeExit);
elements["verifier-cancel-unstake-button"].addEventListener("click", cancelVerifierStakeExit);
elements["verifier-withdraw-stake-button"].addEventListener("click", withdrawVerifierStakeExit);
elements["verifier-withdraw-rewards-button"].addEventListener("click", withdrawVerifierEarnings);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && autoWorkerRequested && autoWorkerLockHeld) {
    void acquireAutoWorkerWakeLock();
    scheduleAutoWorker(0);
  }
});
window.addEventListener("online", () => {
  if (autoWorkerRequested && autoWorkerLockHeld) scheduleAutoWorker(0);
});
elements["participant-id"].textContent = participantId;
elements["model-pack-size"].textContent = formatBytes(modelCatalog.downloadBytes);
elements["wallet-button"].disabled = true;
elements["wallet-button"].textContent = "Loading network…";
void initializeNetworkConsole();
void refreshAlphaReport();

async function runCheck() {
  setBusy(true);
  resetView({ keepBusy: true });
  setRunState("RUNNING", "active");
  setStep("gpu", "active");
  logEvent("Starting local capability check.");

  let result;
  let modelProbe;
  try {
    result = await inspectAndBenchmark({ onEvent: logEvent });
    benchmarkResult = result;
    setStep("gpu", result.capability.webgpuAvailable ? "complete" : "error");
    setStep("compute", result.benchmark.valid ? "complete" : "error");

    setStep("manifest", "active");
    try {
      modelProbe = await probeFluxRuntime({ onEvent: logEvent });
      currentModelProbe = modelProbe;
      setStep("manifest", "complete");
      setStep("render", modelProbe.runtimeAvailable ? "ready" : "locked");
    } catch (error) {
      modelProbe = { manifestValid: false, runtimeAvailable: false, error: getUserFacingError(error), canonicalRender: null };
      setStep("manifest", "error");
      logEvent(`Manifest check failed: ${getUserFacingError(error)}`);
    }

    receipt = createReceipt({
      ...result,
      modelProbe,
      events,
      userAgent: navigator.userAgent,
      secureContext: globalThis.isSecureContext
    });
    renderResult(receipt);
    await prepareModelPack();
  } catch (error) {
    logEvent(`Unexpected failure: ${getUserFacingError(error)}`);
    showFatal(getUserFacingError(error));
  } finally {
    setBusy(false);
  }
}

function renderResult(nextReceipt) {
  const { capability, benchmark, modelProbe, qualification } = nextReceipt;
  elements["metric-gflops"].textContent = benchmark.valid ? benchmark.gflops.toFixed(1) : "FAIL";
  elements["metric-storage"].textContent = formatBytes(capability.limits?.maxStorageBufferBindingSize);
  elements["metric-f16"].textContent = capability.features?.includes("shader-f16") ? "YES" : "NO";
  elements["metric-runtime"].textContent = modelProbe.runtimeAvailable ? "READY" : "ABSENT";
  elements["adapter-name"].textContent = capability.adapter?.description || capability.reason || "Unavailable";
  elements["receipt-id"].textContent = nextReceipt.id;
  elements["verdict-title"].textContent = qualification.title;
  elements["verdict-detail"].textContent = qualification.detail;
  elements["verdict-light"].dataset.tone = qualification.tone;
  setRunState(qualification.tier, qualification.tone);

  elements["model-status"].innerHTML = modelProbe.manifestValid
    ? `<span class="model-glyph" aria-hidden="true">${modelProbe.runtimeAvailable ? "◆" : "◇"}</span><div><strong>${modelProbe.runtimeAvailable ? "Runtime ready" : "Contract ready · runtime absent"}</strong><p>${modelProbe.runtimeAvailable ? "Canonical render can be enabled next." : "No weights were downloaded. This device is a hardware candidate, not render-qualified."}</p></div>`
    : `<span class="model-glyph" aria-hidden="true">!</span><div><strong>Manifest failed</strong><p>${escapeHtml(modelProbe.error || "Unknown model manifest error")}</p></div>`;
  elements["render-step-detail"].textContent = modelProbe.runtimeAvailable ? "Ready for integration" : "Runtime adapter needed";
  elements["copy-button"].disabled = false;
  elements["export-button"].disabled = false;
  updateAlphaEvidencePanel();
  renderQualificationGate();
  void refreshQueue();
  logEvent(`Receipt ${nextReceipt.id} created: ${qualification.tier}.`);
}

function resetView({ keepBusy = false } = {}) {
  receipt = null;
  events = [];
  benchmarkResult = null;
  currentModelProbe = null;
  if (canonicalImageUrl) URL.revokeObjectURL(canonicalImageUrl);
  canonicalImageUrl = null;
  for (const step of document.querySelectorAll("[data-step]")) {
    step.dataset.state = step.dataset.step === "render" ? "locked" : "idle";
  }
  elements["metric-gflops"].textContent = "—";
  elements["metric-storage"].textContent = "—";
  elements["metric-f16"].textContent = "—";
  elements["metric-runtime"].textContent = "—";
  elements["adapter-name"].textContent = "Waiting for permission";
  elements["receipt-id"].textContent = "NO RECEIPT";
  elements["verdict-title"].textContent = "Unknown capability";
  elements["verdict-detail"].textContent = "No hardware information has left your browser.";
  elements["verdict-light"].dataset.tone = "neutral";
  elements["event-log"].innerHTML = "<li><time>—</time><span>Ready. Start a device check when you are ready.</span></li>";
  elements["model-status"].innerHTML = '<span class="model-glyph" aria-hidden="true">◇</span><div><strong>Manifest not checked</strong><p>The benchmark will validate the model-pack contract. Weights are not downloaded in this spike.</p></div>';
  elements["model-pack-progress"].value = 0;
  elements["model-pack-detail"].textContent = "Run the device check before installing the pinned model pack.";
  elements["model-pack-button"].textContent = "Check device first";
  elements["model-pack-button"].disabled = true;
  elements["canonical-render-button"].disabled = true;
  elements["canonical-output"].hidden = true;
  elements["copy-button"].disabled = true;
  elements["export-button"].disabled = true;
  updateAlphaEvidencePanel();
  if (!keepBusy) {
    setRunState("Not tested", "neutral");
    setBusy(false);
  }
}

async function prepareModelPack() {
  if (!receipt?.capability) return;
  elements["model-pack-detail"].textContent = "Checking browser storage and cached artifacts…";
  try {
    const status = await inspectModelPack(modelCatalog, receipt.capability);
    elements["model-pack-progress"].value = status.totalArtifacts
      ? status.installedArtifacts / status.totalArtifacts
      : 0;
    if (status.installed) {
      elements["model-pack-detail"].textContent = `${formatBytes(status.installedBytes)} installed in this browser. Preparing the local runtime…`;
      elements["model-pack-button"].textContent = "Model pack installed";
      elements["model-pack-button"].disabled = true;
      await enableFluxRuntime();
      return;
    }
    const installed = `${status.installedArtifacts || 0}/${status.totalArtifacts || modelCatalog.artifacts.length} files cached`;
    if (!status.eligible) {
      const failed = Object.entries(status.checks || {}).filter(([, passed]) => !passed).map(([name]) => name).join(", ");
      elements["model-pack-detail"].textContent = `${installed}. Install blocked by: ${failed || "browser requirements"}.`;
      elements["model-pack-button"].textContent = "Requirements not met";
      elements["model-pack-button"].disabled = true;
      return;
    }
    elements["model-pack-detail"].textContent = `${installed}. Downloads ${formatBytes(modelCatalog.downloadBytes)} once and stores it locally.`;
    elements["model-pack-button"].textContent = "Install model pack";
    elements["model-pack-button"].disabled = false;
  } catch (error) {
    elements["model-pack-detail"].textContent = `Model pack check failed: ${getUserFacingError(error)}`;
    elements["model-pack-button"].disabled = true;
  }
}

async function downloadModelPack() {
  if (!receipt?.capability || activeOperationController) return;
  const controller = beginAbortableOperation();
  elements["model-pack-button"].disabled = true;
  elements["canonical-render-button"].disabled = true;
  logEvent(`Installing pinned ${formatBytes(modelCatalog.downloadBytes)} model pack.`);
  try {
    const status = await installModelPack(modelCatalog, {
      capability: receipt.capability,
      signal: controller.signal,
      onProgress(progress) {
        const loaded = progress.completedBytes + (progress.artifactBytes || 0);
        elements["model-pack-progress"].value = Math.min(1, loaded / progress.totalBytes);
        const name = progress.artifact?.path?.split("/").at(-1) || "artifact";
        elements["model-pack-detail"].textContent = `${progress.phase}: ${name} · ${formatBytes(loaded)} / ${formatBytes(progress.totalBytes)}`;
      }
    });
    elements["model-pack-progress"].value = 1;
    elements["model-pack-detail"].textContent = `${formatBytes(status.installedBytes)} installed. Preparing ONNX Runtime WebGPU…`;
    elements["model-pack-button"].textContent = "Model pack installed";
    logEvent("Pinned model pack installed in browser Cache Storage.");
    await enableFluxRuntime();
  } catch (error) {
    elements["model-pack-detail"].textContent = controller.signal.aborted
      ? "Model-pack installation stopped. Already cached files are kept for a later resume."
      : `Install stopped: ${getUserFacingError(error)}`;
    elements["model-pack-button"].textContent = "Resume installation";
    elements["model-pack-button"].disabled = false;
    logEvent(`Model pack installation stopped: ${getUserFacingError(error)}`);
  } finally {
    endAbortableOperation(controller);
  }
}

async function enableFluxRuntime() {
  const { installFlux2WebGpuAdapter } = await import("./src/flux2-webgpu-adapter.js");
  installFlux2WebGpuAdapter({ catalog: modelCatalog, capability: receipt.capability });
  currentModelProbe = await probeFluxRuntime({ onEvent: logEvent });
  elements["metric-runtime"].textContent = "READY";
  elements["model-status"].innerHTML = '<span class="model-glyph" aria-hidden="true">◆</span><div><strong>Runtime ready</strong><p>The pinned model pack is local. Run the canonical render to qualify this device.</p></div>';
  elements["model-pack-detail"].textContent = "Runtime adapter ready. The first render may take several minutes and can use substantial GPU memory.";
  elements["canonical-render-button"].disabled = false;
  elements["render-step-detail"].textContent = "Ready to render";
  setStep("render", "ready");
  await restoreQualificationGrant();
  renderQualificationGate();
}

async function renderCanonicalWorkload() {
  if (!currentModelProbe?.runtimeAvailable || !benchmarkResult || activeOperationController) return;
  const controller = beginAbortableOperation();
  elements["canonical-render-button"].disabled = true;
  elements["model-pack-button"].disabled = true;
  setStep("render", "active");
  logEvent("Starting the canonical FLUX.2 Klein render.");
  const renderStartedAt = performance.now();
  try {
    const canonicalRender = await runCanonicalRender(currentModelProbe, {
      signal: controller.signal,
      onProgress(progress) {
        const message = progress?.message || String(progress);
        elements["model-pack-detail"].textContent = message;
        if (message) logEvent(message);
      }
    });
    if (canonicalImageUrl) URL.revokeObjectURL(canonicalImageUrl);
    canonicalImageUrl = URL.createObjectURL(canonicalRender.image);
    elements["canonical-image"].src = canonicalImageUrl;
    elements["canonical-caption"].textContent = `${(canonicalRender.durationMs / 1000).toFixed(1)} s · sha256:${canonicalRender.outputHash}`;
    elements["canonical-output"].hidden = false;
    currentModelProbe = { ...currentModelProbe, canonicalRender: {
      success: canonicalRender.success,
      durationMs: canonicalRender.durationMs,
      outputHash: canonicalRender.outputHash,
      runtimeId: canonicalRender.runtimeId
    } };
    receipt = createReceipt({
      ...benchmarkResult,
      modelProbe: currentModelProbe,
      events,
      userAgent: navigator.userAgent,
      secureContext: globalThis.isSecureContext
    });
    renderResult(receipt);
    try {
      await registerQualification(canonicalRender.image, canonicalRender, receipt);
    } catch (qualificationError) {
      logEvent(`Canonical render succeeded, but qualification registration failed: ${qualificationError.message}`);
    }
    setStep("render", "complete");
    elements["render-step-detail"].textContent = "Output recorded";
    elements["model-pack-detail"].textContent = contributorQualification?.state === "qualified"
      ? "Canonical output recorded and verifier-backed contributor grant issued."
      : "Canonical output recorded; contributor grant is awaiting verifier quorum.";
  } catch (error) {
    const durationMs = Math.max(0, performance.now() - renderStartedAt);
    currentModelProbe = { ...currentModelProbe, canonicalRender: {
      success: false,
      durationMs,
      runtimeId: currentModelProbe?.runtimeId,
      failureCode: classifyRenderFailure(error, controller.signal.aborted)
    } };
    receipt = createReceipt({
      ...benchmarkResult,
      modelProbe: currentModelProbe,
      events,
      userAgent: navigator.userAgent,
      secureContext: globalThis.isSecureContext
    });
    renderResult(receipt);
    setStep("render", "error");
    elements["model-pack-detail"].textContent = controller.signal.aborted
      ? "Canonical render stopped after the current GPU operation released."
      : `Render failed: ${getUserFacingError(error)}`;
    elements["canonical-render-button"].disabled = false;
    logEvent(`Canonical render failed: ${getUserFacingError(error)}`);
  } finally {
    endAbortableOperation(controller);
  }
}

async function registerQualification(image, canonicalRender, qualificationReceipt) {
  elements["model-pack-detail"].textContent = "Registering canonical output with the coordinator…";
  const response = await fetch("/api/qualifications", {
    method: "POST",
    headers: {
      "content-type": "image/png",
      "x-participant-id": participantId,
      "x-receipt-id": qualificationReceipt.id,
      "x-runtime-id": canonicalRender.runtimeId,
      "x-output-hash": canonicalRender.outputHash,
      ...(walletSession?.sessionToken ? { "x-wallet-session": walletSession.sessionToken } : {})
    },
    body: image
  });
  const created = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(created.error?.message || `Qualification registration returned HTTP ${response.status}.`);
  contributorQualification = {
    id: created.qualification.id,
    state: created.qualification.state,
    expiresAt: created.qualification.expiresAt,
    accessToken: created.accessToken,
    grantToken: created.grantToken
  };
  saveQualificationGrant(participantId, contributorQualification);
  renderQualificationGate();
  logEvent(`Qualification ${shortId(contributorQualification.id)} awaiting verifier quorum.`);
  for (let attempt = 0; attempt < 30 && contributorQualification.state === "pending"; attempt += 1) {
    await new Promise((resolve) => window.setTimeout(resolve, 1_000));
    const statusResponse = await fetch(`/api/qualifications/${encodeURIComponent(contributorQualification.id)}`, {
      headers: { "x-qualification-access-token": contributorQualification.accessToken }
    });
    const status = await statusResponse.json().catch(() => ({}));
    if (!statusResponse.ok) throw new Error(status.error?.message || `Qualification status returned HTTP ${statusResponse.status}.`);
    contributorQualification.state = status.state;
    contributorQualification.expiresAt = status.expiresAt;
    saveQualificationGrant(participantId, contributorQualification);
  }
  renderQualificationGate();
  await refreshQueue();
  if (contributorQualification.state === "qualified") {
    logEvent(`Verifier quorum issued contributor grant ${shortId(contributorQualification.id)}.`);
  } else if (contributorQualification.state === "rejected") {
    throw new Error("Canonical output was rejected by verifier quorum.");
  }
}

function beginAbortableOperation() {
  if (activeOperationController) throw new Error("Another model operation is already active.");
  const controller = new AbortController();
  activeOperationController = controller;
  elements["abort-operation-button"].disabled = false;
  elements["delete-model-button"].disabled = true;
  return controller;
}

function endAbortableOperation(controller) {
  if (activeOperationController !== controller) return;
  activeOperationController = null;
  elements["abort-operation-button"].disabled = true;
  elements["delete-model-button"].disabled = false;
}

function abortActiveOperation() {
  if (!activeOperationController) return;
  activeOperationController.abort("Stopped by the contributor.");
  elements["abort-operation-button"].disabled = true;
  elements["model-pack-detail"].textContent = "Stop requested. The current GPU command must finish before memory can be released safely.";
  logEvent("Contributor requested a stop for the active model operation.");
}

async function removeModelPack() {
  if (activeOperationController) return;
  if (!globalThis.confirm("Delete the cached 5.23 GB model pack from this browser?")) return;
  elements["delete-model-button"].disabled = true;
  try {
    await deleteModelPack();
    delete globalThis.SHAREMYAI_FLUX_ADAPTER;
    contributorQualification = null;
    currentModelProbe = currentModelProbe ? { ...currentModelProbe, runtimeAvailable: false, runtimeId: null, canonicalRender: null } : null;
    elements["model-pack-progress"].value = 0;
    elements["canonical-render-button"].disabled = true;
    elements["canonical-output"].hidden = true;
    elements["metric-runtime"].textContent = "ABSENT";
    elements["model-pack-detail"].textContent = "Cached model artifacts were deleted from this browser.";
    elements["model-pack-button"].textContent = receipt?.capability ? "Install model pack" : "Check device first";
    elements["model-pack-button"].disabled = !receipt?.capability;
    renderQualificationGate();
    logEvent("Local model pack deleted by the contributor.");
  } catch (error) {
    elements["model-pack-detail"].textContent = `Model-pack deletion failed: ${getUserFacingError(error)}`;
  } finally {
    elements["delete-model-button"].disabled = false;
  }
}

function setBusy(busy) {
  elements["run-button"].disabled = busy;
  elements["run-button"].querySelector("span:first-child").textContent = busy ? "Measuring GPU…" : "Run device check";
  elements["reset-button"].disabled = busy;
}

function setRunState(label, tone) {
  elements["run-state"].textContent = label;
  elements["run-state"].dataset.tone = tone;
}

function setStep(name, state) {
  const step = document.querySelector(`[data-step="${name}"]`);
  if (step) step.dataset.state = state;
}

function logEvent(message) {
  const timestamp = new Date().toISOString();
  events.push({ timestamp, message });
  const placeholder = elements["event-log"].querySelector("time")?.textContent === "—";
  if (placeholder) elements["event-log"].replaceChildren();
  const item = document.createElement("li");
  item.innerHTML = `<time datetime="${timestamp}">${new Date(timestamp).toLocaleTimeString([], { hour12: false })}</time><span>${escapeHtml(message)}</span>`;
  elements["event-log"].append(item);
  elements["event-log"].scrollTop = elements["event-log"].scrollHeight;
}

async function copyReceipt() {
  if (!receipt) return;
  await navigator.clipboard.writeText(serializeReceipt(receipt));
  const original = elements["copy-button"].textContent;
  elements["copy-button"].textContent = "Copied";
  window.setTimeout(() => { elements["copy-button"].textContent = original; }, 1400);
}

function exportReceipt() {
  if (!receipt) return;
  const blob = new Blob([serializeReceipt(receipt)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${receipt.id}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

async function submitAlphaEvidence() {
  const evidence = createAlphaEvidence(receipt);
  if (!evidence) return;
  elements["alpha-submit-button"].disabled = true;
  elements["alpha-submit-button"].textContent = "Submitting minimized result…";
  elements["alpha-message"].textContent = "Sending the disclosed evidence fields to the cohort report.";
  elements["alpha-message"].dataset.tone = "neutral";
  try {
    const response = await fetch("/api/alpha/evidence", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(networkConfig?.alphaEvidence?.invitesRequired
          ? { "x-alpha-invite": elements["alpha-invite-input"].value.trim() }
          : {})
      },
      body: JSON.stringify(evidence)
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error?.message || `Evidence submission returned HTTP ${response.status}.`);
    localStorage.setItem(`sharemyai:alpha:${receipt.id}`, result.evidenceId);
    elements["alpha-invite-input"].value = "";
    elements["alpha-evidence"].dataset.state = "submitted";
    elements["alpha-submit-button"].textContent = result.deduplicated ? "Result already counted" : "Anonymous result counted";
    elements["alpha-message"].textContent = `${result.deduplicated ? "Duplicate suppressed" : "Evidence accepted"} · ${shortId(result.evidenceId)} · retained 90 days.`;
    elements["alpha-message"].dataset.tone = "success";
    renderAlphaReport(result.report);
    logEvent(`Anonymous alpha evidence ${shortId(result.evidenceId)} ${result.deduplicated ? "deduplicated" : "accepted"}.`);
  } catch (error) {
    elements["alpha-submit-button"].disabled = false;
    elements["alpha-submit-button"].textContent = "Submit anonymous alpha result";
    elements["alpha-message"].textContent = getUserFacingError(error);
    elements["alpha-message"].dataset.tone = "error";
  }
}

async function refreshAlphaReport() {
  try {
    const response = await fetch("/api/alpha/report");
    const report = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(report.error?.message || `Report returned HTTP ${response.status}.`);
    renderAlphaReport(report);
  } catch {
    elements["alpha-run-count"].textContent = "— / 30";
    elements["alpha-completion"].textContent = "Offline";
    elements["alpha-device-count"].textContent = "—";
    elements["alpha-paid-count"].textContent = "— / 10";
  }
}

function renderAlphaReport(report) {
  if (!report) return;
  elements["alpha-run-count"].textContent = `${report.totalRuns} / ${report.targetRuns}`;
  elements["alpha-completion"].textContent = report.completionRate == null
    ? "—"
    : `${(report.completionRate * 100).toFixed(1)}%`;
  elements["alpha-device-count"].textContent = `${report.deviceClasses} / ${report.targetDeviceClasses}`;
  elements["alpha-paid-count"].textContent = `${report.paidWorkAccepted} / ${report.targetPaidWorkRuns}`;
}

function updateAlphaEvidencePanel() {
  const evidence = createAlphaEvidence(receipt);
  const priorId = receipt?.id ? localStorage.getItem(`sharemyai:alpha:${receipt.id}`) : null;
  const inviteRequired = networkConfig?.alphaEvidence?.invitesRequired === true;
  elements["alpha-invite-row"].hidden = !inviteRequired;
  if (!evidence) {
    elements["alpha-evidence"].dataset.state = "locked";
    elements["alpha-submit-button"].disabled = true;
    elements["alpha-submit-button"].textContent = "Canonical attempt required";
    elements["alpha-message"].textContent = "No data is submitted automatically. Complete or stop one canonical render to opt in.";
    elements["alpha-message"].dataset.tone = "neutral";
    return;
  }
  if (priorId) {
    elements["alpha-evidence"].dataset.state = "submitted";
    elements["alpha-submit-button"].disabled = true;
    elements["alpha-submit-button"].textContent = "Anonymous result counted";
    elements["alpha-message"].textContent = `Previously submitted · ${shortId(priorId)}.`;
    elements["alpha-message"].dataset.tone = "success";
    return;
  }
  elements["alpha-evidence"].dataset.state = "ready";
  const inviteReady = !inviteRequired || /^smai_[a-f0-9]{64}$/.test(elements["alpha-invite-input"].value.trim());
  elements["alpha-submit-button"].disabled = !inviteReady;
  elements["alpha-submit-button"].textContent = inviteReady ? "Submit anonymous alpha result" : "Enter one-use invitation";
  elements["alpha-message"].textContent = `${evidence.outcome.status} · ${evidence.device.vendor}/${evidence.device.architecture} · ${evidence.outcome.durationMs} ms.`;
  elements["alpha-message"].dataset.tone = evidence.outcome.status === "success" ? "success" : "warning";
}

function showFatal(message) {
  setRunState("FAILED", "error");
  elements["verdict-title"].textContent = "Check failed";
  elements["verdict-detail"].textContent = message;
  elements["verdict-light"].dataset.tone = "error";
}

function detectBrowser() {
  const userAgent = navigator.userAgent;
  if (userAgent.includes("Edg/")) return "Microsoft Edge";
  if (userAgent.includes("Chrome/")) return "Google Chrome / Chromium";
  if (userAgent.includes("Firefox/")) return "Mozilla Firefox";
  if (userAgent.includes("Safari/")) return "Apple Safari";
  return "Unknown browser";
}

function escapeHtml(value) {
  return String(value).replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" }[character]));
}

function chainLabel() {
  if (networkConfig?.mode === "local-simulation") return "Local simulation";
  if (networkConfig?.mode === "points-alpha") return "Points alpha";
  return networkConfig?.chain?.shortName || networkConfig?.chain?.name || "configured network";
}

function tokenSymbol() {
  return networkConfig?.currency?.symbol || "tokens";
}

function formatTokenAmount(value, maximumFractionDigits = 4) {
  const decimals = Number(networkConfig?.currency?.decimals ?? 18);
  const units = BigInt(value || 0);
  if (decimals === 0) return units.toString();
  const base = 10n ** BigInt(decimals);
  const whole = units / base;
  const fraction = (units % base).toString().padStart(decimals, "0").slice(0, maximumFractionDigits).replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

/**
 * Puts the wallet button into whichever state the coordinator has earned.
 *
 * Called as soon as the network answers and again when the rest of the console
 * settles, because the second one is not guaranteed to happen.
 */
function releaseWalletButton() {
  if (walletConnectionInFlight) return;
  const connected = elements["wallet-button"].dataset.connected === "true";
  elements["wallet-button"].disabled = !networkConfig;
  if (!networkConfig) {
    elements["wallet-control"].dataset.state = "error";
    elements["wallet-control"].dataset.stage = "network";
    elements["wallet-button"].textContent = "Network unavailable";
    elements["wallet-status"].textContent = "The coordinator is unavailable. Refresh before connecting a wallet.";
    elements["wallet-status"].hidden = false;
  } else if (!connected && elements["wallet-control"].dataset.state !== "error") {
    elements["wallet-button"].textContent = "Connect wallet";
  }
}

async function initializeNetworkConsole() {
  try {
    const network = await apiRequest("/api/network");
    networkConfig = network;
    // Enabled here, not in the finally below. Everything after this point
    // awaits something else, and one of those is a wallet provider call that a
    // browser extension can leave pending forever. When that happened the
    // finally never ran and the console's primary action stayed disabled with
    // "Loading network" on it, on a page whose own API had answered fine.
    releaseWalletButton();
    elements["network-mode"].textContent = network.mode.replaceAll("-", " ");
    elements["network-mode"].dataset.tone = "warning";
    configureNetworkModeUi();
    configureNetworkCopy();
    updateAlphaEvidencePanel();
    await syncWalletIdentity({ requestAccounts: false });
    await restoreQualificationGrant();
    await Promise.all([refreshBalance(), refreshQueue(), refreshConsumerResults(), refreshWorkerHistory(), refreshAdminAccess(), refreshVerifierStatus()]);
  } catch (error) {
    elements["network-mode"].textContent = "Coordinator offline";
    elements["network-mode"].dataset.tone = "error";
    setFormMessage(getUserFacingError(error, "The coordinator is unavailable. Refresh and try again."), "error");
  } finally {
    releaseWalletButton();
  }
}

async function connectWallet() {
  elements["wallet-button"].disabled = true;
  walletConnectionInFlight = true;
  try {
    setWalletStage("detecting", "Detecting wallets…", "Choose an installed EVM wallet to begin.");
    const entry = await prepareWalletProvider({ interactive: true });
    if (!entry) throw new Error("No EIP-1193 wallet was detected in this browser.");

    const usesChain = Boolean(networkConfig?.chain);
    const totalSteps = usesChain ? 3 : 2;
    setWalletStage("account", "Connect account…", `Step 1/${totalSteps} · Approve the account connection in ${entry.info.name}.`);
    const address = await syncWalletIdentity({ requestAccounts: true, authenticate: false });
    if (!address) throw new Error("Wallet returned no account.");

    if (usesChain) {
      setWalletStage("network", "Switch network…", `Step 2/3 · Approve ${chainLabel()} in your wallet.`);
      await ensureRobinhoodTestnet();
    }

    if (requiresWalletSession() && !walletSession) {
      setWalletStage("signature", "Sign to finish…", `Step ${totalSteps}/${totalSteps} · Sign the free login message. This is not a transaction.`);
      await createWalletSession(address);
    }

    await restoreQualificationGrant();
    await Promise.all([refreshBalance(), refreshQueue(), refreshConsumerResults(), refreshWorkerHistory(), refreshAdminAccess(), refreshVerifierStatus()]);
    setWalletSuccess(address);
    setFormMessage(networkConfig?.authentication?.walletSessionRequired
      ? "Wallet connected and authenticated with a non-transaction EIP-712 signature."
      : "Wallet connected as the local-alpha identity. No signature or transaction was requested.", "success");
  } catch (error) {
    const message = friendlyWalletError(error);
    setWalletFailure(message);
    setFormMessage(`Wallet connection failed: ${message}`, "error");
  } finally {
    walletConnectionInFlight = false;
    elements["wallet-button"].disabled = false;
  }
}

function requiresWalletSession() {
  return networkConfig?.authentication?.walletSessionRequired === true || networkConfig?.admin?.enabled === true;
}

/**
 * A wallet read that cannot hang the page.
 *
 * `eth_accounts` and `eth_chainId` answer from the extension's own state and
 * should return immediately. When two wallet extensions fight over
 * window.ethereum one of them can accept the call and never settle it, and
 * anything waiting on that promise waits forever. That is what left the console
 * showing a disabled "Loading network" button on a page whose API was fine.
 *
 * Only the silent reads get this. `eth_requestAccounts`, signing, and switching
 * chains all wait on a person deciding something in a popup, and a timeout
 * there would cancel work the visitor is in the middle of.
 */
async function readFromWallet(method, timeoutMs = 8000) {
  let timer;
  try {
    return await Promise.race([
      walletProvider.request({ method }),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(`The wallet did not answer ${method}. Another extension may be interfering.`)), timeoutMs);
      })
    ]);
  } finally {
    clearTimeout(timer);
  }
}

async function syncWalletIdentity({ requestAccounts, authenticate = false }) {
  const entry = await prepareWalletProvider({ interactive: false });
  if (!entry) {
    resetWalletIdentity();
    if (requestAccounts) throw new Error("No EIP-1193 wallet was detected in this browser.");
    return null;
  }
  // Asking for accounts opens a popup and waits on a person; reading them does
  // not, and must not be allowed to wait forever.
  const accounts = requestAccounts
    ? await walletProvider.request({ method: "eth_requestAccounts" })
    : await readFromWallet("eth_accounts");
  const address = accounts?.[0];
  if (!address) {
    resetWalletIdentity();
    if (requestAccounts) throw new Error("Wallet returned no account.");
    return null;
  } else {
    participantId = String(address).toLowerCase();
    elements["wallet-button"].textContent = shortId(participantId);
    elements["wallet-button"].dataset.connected = "true";
    if (requiresWalletSession()) {
      await restoreWalletSession(participantId);
      if (authenticate && !walletSession) await createWalletSession(participantId);
    } else {
      walletSession = null;
    }
  }
  elements["participant-id"].textContent = participantId;
  await restoreQualificationGrant();
  return participantId;
}

async function ensureRobinhoodTestnet() {
  if (!walletProvider?.request) throw new Error("Select a wallet before switching networks.");
  if (!networkConfig?.chain) return;
  const currentChain = await readFromWallet("eth_chainId");
  if (String(currentChain).toLowerCase() === networkConfig.chain.chainIdHex.toLowerCase()) return;
  try {
    await walletProvider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: networkConfig.chain.chainIdHex }]
    });
  } catch (error) {
    if (Number(error?.code) !== 4902) throw error;
    await walletProvider.request({
      method: "wallet_addEthereumChain",
      params: [{
        chainId: networkConfig.chain.chainIdHex,
        chainName: networkConfig.chain.name,
        nativeCurrency: networkConfig.chain.nativeCurrency,
        rpcUrls: [networkConfig.chain.rpcUrl],
        blockExplorerUrls: [networkConfig.chain.explorerUrl]
      }]
    });
  }
}

async function prepareWalletProvider({ interactive }) {
  walletDiscovery.request();
  if (interactive) await new Promise((resolve) => window.setTimeout(resolve, 120));
  const providers = walletDiscovery.list();
  if (providers.length === 0) {
    if (interactive) throw new Error("No EIP-1193 wallet was detected in this browser.");
    return null;
  }

  let entry;
  if (interactive && providers.length > 1) entry = await chooseWalletProvider(providers);
  else if (walletProviderEntry && providers.some((candidate) => candidate.provider === walletProviderEntry.provider)) entry = walletProviderEntry;
  else entry = selectPreferredProvider(providers, loadProviderPreference());

  if (!entry) {
    const error = new Error("Wallet selection was cancelled.");
    error.code = 4001;
    throw error;
  }
  walletProviderEntry = entry;
  walletProvider = entry.provider;
  saveProviderPreference(entry.key);
  bindWalletProvider(entry.provider);
  return entry;
}

function chooseWalletProvider(providers) {
  const dialog = elements["wallet-provider-dialog"];
  const list = elements["wallet-provider-list"];
  list.replaceChildren();

  return new Promise((resolve) => {
    let selected = null;
    for (const entry of providers) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "wallet-provider-option";
      const mark = document.createElement("span");
      mark.className = "wallet-provider-mark";
      mark.setAttribute("aria-hidden", "true");
      mark.textContent = entry.info.name.slice(0, 1);
      const copy = document.createElement("span");
      const name = document.createElement("strong");
      name.textContent = entry.info.name;
      const detail = document.createElement("small");
      detail.textContent = entry.info.rdns || "Injected browser wallet";
      copy.append(name, detail);
      button.append(mark, copy);
      button.addEventListener("click", () => {
        selected = entry;
        dialog.close("selected");
      });
      list.append(button);
    }
    dialog.addEventListener("close", () => resolve(selected), { once: true });
    dialog.showModal();
  });
}

function bindWalletProvider(provider) {
  if (!provider?.on || boundWalletProviders.has(provider)) return;
  boundWalletProviders.add(provider);
  provider.on("accountsChanged", () => {
    workerSession = null;
    stopAutoWorker("Auto-work stopped because the wallet account changed.");
    void handleWalletProviderChange("account");
  });
  provider.on("chainChanged", () => {
    workerSession = null;
    stopAutoWorker("Auto-work stopped because the wallet network changed.");
    void handleWalletProviderChange("chain");
  });
  provider.on("disconnect", () => {
    workerSession = null;
    stopAutoWorker("Auto-work stopped because the wallet disconnected.");
    resetWalletIdentity();
    setWalletFailure("Wallet disconnected. Reopen it and connect again.");
  });
}

async function handleWalletProviderChange(kind) {
  if (walletConnectionInFlight) return;
  try {
    const address = await syncWalletIdentity({ requestAccounts: false });
    if (!address) return;
    if (kind === "chain" && networkConfig?.chain && !await isConfiguredChain()) {
      setWalletFailure(`Wrong network. Click to switch back to ${chainLabel()}.`);
      return;
    }
    if (walletSession) setWalletSuccess(address);
    else setWalletFailure("Account changed. Click to authenticate this wallet for the alpha session.");
    await Promise.allSettled([refreshBalance(), refreshQueue(), refreshConsumerResults(), refreshWorkerHistory(), refreshAdminAccess(), refreshVerifierStatus()]);
  } catch (error) {
    setWalletFailure(friendlyWalletError(error));
  }
}

async function isConfiguredChain() {
  if (!networkConfig?.chain) return true;
  if (!walletProvider?.request || !networkConfig?.chain?.chainIdHex) return false;
  try {
    const currentChain = await readFromWallet("eth_chainId");
    return String(currentChain).toLowerCase() === networkConfig.chain.chainIdHex.toLowerCase();
  } catch {
    return false;
  }
}

function resetWalletIdentity() {
  walletSession = null;
  workerSession = null;
  participantId = getParticipantId();
  elements["participant-id"].textContent = participantId;
  elements["wallet-button"].textContent = "Connect wallet";
  elements["wallet-button"].dataset.connected = "false";
  elements["wallet-control"].dataset.state = "idle";
  elements["wallet-status"].hidden = true;
  renderAdminLocked("Connect the configured operator wallet to unlock network controls.");
  renderVerifierUnavailable("Connect and authenticate a wallet to inspect its verifier identity.", "waiting");
  renderWorkerRewardState(null);
}

function setWalletStage(stage, label, message) {
  elements["wallet-control"].dataset.state = "active";
  elements["wallet-control"].dataset.stage = stage;
  elements["wallet-button"].dataset.connected = "false";
  elements["wallet-button"].textContent = label;
  elements["wallet-status"].textContent = message;
  elements["wallet-status"].hidden = false;
}

function setWalletSuccess(address) {
  elements["wallet-control"].dataset.state = "success";
  elements["wallet-control"].dataset.stage = "complete";
  elements["wallet-button"].dataset.connected = "true";
  elements["wallet-button"].textContent = shortId(address);
  elements["wallet-status"].textContent = `Connected with ${walletProviderEntry?.info?.name || "wallet"} · authenticated for this alpha session.`;
  elements["wallet-status"].hidden = false;
  window.setTimeout(() => {
    if (elements["wallet-control"].dataset.state === "success") elements["wallet-status"].hidden = true;
  }, 4_000);
}

function setWalletFailure(message) {
  elements["wallet-control"].dataset.state = "error";
  elements["wallet-control"].dataset.stage = "error";
  elements["wallet-button"].dataset.connected = "false";
  elements["wallet-button"].textContent = "Retry wallet";
  elements["wallet-status"].textContent = message;
  elements["wallet-status"].hidden = false;
}

async function requestTestCredits() {
  elements["faucet-button"].disabled = true;
  try {
    if (networkConfig?.settlement?.enabled) {
      const { claimTestTokens, getEscrowWalletStatus, withdrawEscrowBalance } = await import("./src/escrow-browser-client.js");
      const status = await getEscrowWalletStatus(participantId, networkConfig, walletProvider);
      if (status.pending === 0n && networkConfig?.currency?.faucetEnabled !== true) {
        setFormMessage(`No settled ${tokenSymbol()} rewards are ready to withdraw.`, "warning");
        return;
      }
      const transactionHash = status.pending > 0n
        ? await withdrawEscrowBalance(networkConfig, walletProvider)
        : await claimTestTokens(networkConfig, walletProvider);
      setFormMessage(status.pending > 0n
        ? `${tokenSymbol()} withdrawal confirmed: ${shortId(transactionHash)}.`
        : `Test ${tokenSymbol()} minted: ${shortId(transactionHash)}.`, "success");
      await refreshBalance();
      return;
    }
    const account = await apiRequest("/api/faucet", {
      method: "POST",
      body: JSON.stringify({ accountId: participantId })
    });
    elements["credit-balance"].textContent = account.balance;
    const amount = networkConfig?.currency?.starterGrant || 100;
    setFormMessage(`${amount} ${tokenSymbol()} issued. They cannot be transferred, sold, or redeemed.`, "success");
  } catch (error) {
    setFormMessage(getUserFacingError(error), "error");
    await refreshBalance();
  } finally {
    if (!networkConfig?.settlement?.enabled) elements["faucet-button"].disabled = false;
  }
}

async function refreshBalance() {
  if (networkConfig?.settlement?.enabled) {
    if (!/^0x[0-9a-f]{40}$/.test(participantId)
      || (networkConfig?.authentication?.walletSessionRequired && !walletSession)) {
      elements["credit-balance"].textContent = "—";
      elements["faucet-button"].textContent = "Connect wallet";
      elements["faucet-button"].disabled = true;
      renderWorkerRewardState(null);
      renderWorkerBondState(null);
      return;
    }
    if (!await isConfiguredChain()) {
      elements["credit-balance"].textContent = "—";
      elements["faucet-button"].textContent = "Switch network first";
      elements["faucet-button"].disabled = true;
      renderWorkerRewardState(null);
      renderWorkerBondState(null);
      setWalletFailure(`Wrong network. Click to switch to ${chainLabel()}.`);
      return;
    }
    const { getEscrowWalletStatus } = await import("./src/escrow-browser-client.js");
    const status = await getEscrowWalletStatus(participantId, networkConfig, walletProvider);
    elements["credit-balance"].textContent = Number(status.balanceLabel).toFixed(2);
    const canFaucet = networkConfig?.currency?.faucetEnabled === true && status.faucetClaimed === false;
    elements["faucet-button"].textContent = status.pending > 0n
      ? `Withdraw ${Number(status.pendingLabel).toFixed(2)} ${tokenSymbol()}`
      : canFaucet ? `Mint test ${tokenSymbol()}` : "No reward to withdraw";
    elements["faucet-button"].disabled = status.pending === 0n && !canFaucet;
    renderWorkerRewardState(status);
    renderWorkerBondState(status);
    if (status.gasBalance === 0n) {
      setFormMessage(networkConfig?.production
        ? `This wallet needs ETH on ${chainLabel()} for transaction gas.`
        : `This wallet needs testnet ETH for gas. Use the linked faucet first.`, "warning");
    }
    return;
  }
  if (networkConfig?.authentication?.walletSessionRequired && !walletSession) {
    elements["credit-balance"].textContent = "—";
    elements["faucet-button"].textContent = "Connect wallet";
    elements["faucet-button"].disabled = true;
    renderWorkerRewardState(null);
    renderWorkerBondState(null);
    return;
  }
  const account = await apiRequest(`/api/accounts/${encodeURIComponent(participantId)}`);
  elements["credit-balance"].textContent = account.balance;
  elements["faucet-button"].textContent = account.starterGrantClaimed
    ? "Starter points claimed"
    : `Claim ${networkConfig?.currency?.starterGrant || 100} ${tokenSymbol()}`;
  elements["faucet-button"].disabled = account.starterGrantClaimed;
  renderWorkerRewardState({ localCredits: true });
  renderWorkerBondState(null);
}

function configureNetworkModeUi() {
  const balanceUnit = elements["credit-balance"].nextElementSibling;
  if (networkConfig?.settlement?.enabled) {
    balanceUnit.textContent = tokenSymbol();
    elements["faucet-button"].textContent = "Connect wallet";
    elements["faucet-button"].disabled = true;
    const valueLabel = networkConfig?.production ? "Experimental" : "No-value";
    elements["job-form-message"].textContent = networkConfig?.settlement?.bonding
      ? `${valueLabel} ${tokenSymbol()} rewards use bonded escrow on ${chainLabel()}. Sampled or challenged results require verifier review.`
      : `${valueLabel} ${tokenSymbol()} rewards are escrowed on ${chainLabel()} and settle only after verifier quorum.`;
    return;
  }
  const pointsMode = networkConfig?.mode === "points-alpha";
  balanceUnit.textContent = pointsMode ? tokenSymbol() : "test credits";
  if (networkConfig?.currency?.enabled !== true) {
    elements["create-job-button"].disabled = true;
    elements["faucet-button"].disabled = true;
    elements["job-form-message"].textContent = "Guarded mode is waiting for a configured escrow contract.";
    return;
  }
  elements["create-job-button"].disabled = false;
  elements["faucet-button"].textContent = pointsMode ? "Connect wallet" : "Get test credits";
  elements["faucet-button"].disabled = pointsMode;
  elements["job-form-message"].textContent = pointsMode
    ? "Non-transferable points are held until independent verifiers accept an output. Points have no cash value or promised token redemption."
    : "No-value alpha credits are escrowed until two independent verifiers accept an output.";
}

function configureNetworkCopy() {
  const production = networkConfig?.production === true;
  const pointsMode = networkConfig?.mode === "points-alpha";
  const set = (id, text) => { const element = document.getElementById(id); if (element) element.textContent = text; };
  set("landing-chain", String(chainLabel()));
  set("landing-settlement-copy", pointsMode ? "Verified work earns wallet-bound points" : `Escrow releases ${tokenSymbol()}`);
  set("wallet-network-copy", `Select the extension that should authorize this ${chainLabel()} session.`);
  set("sidebar-chain", chainLabel());
  set("header-stage-tag", production ? "MAINNET EXPERIMENT" : pointsMode ? "POINTS ALPHA" : networkConfig?.mode === "local-simulation" ? "Local" : "TESTNET");
  set("workspace-network-tag", production ? `${String(chainLabel())} · EXPERIMENTAL TOKEN` : pointsMode ? "WALLET POINTS · NON-TRANSFERABLE" : `${String(chainLabel())} · NO-VALUE TOKEN`);
  set("settlement-chain-copy", pointsMode ? "Verifier-gated points ledger" : `${chainLabel()} contract gate`);
  set("app-release-copy", production ? "Public mainnet experiment · capped exposure" : pointsMode ? "Public points experiment · no token issued" : "Private testnet alpha · no production token issued");
  set("landing-footer-network", chainLabel());
  set("landing-footer-value", production ? "Experimental token" : pointsMode ? "Non-transferable points" : "No-value token");
  set("verifier-stake-symbol", tokenSymbol());
  set("landing-footer-stage", production || pointsMode ? "Public experiment" : "Private alpha");
  set("release-stage-pill", production ? "PUBLIC EXPERIMENT" : pointsMode ? "PUBLIC ALPHA" : "Private alpha");
  set("alpha-disclaimer", production
    ? "Public experiment · real transactions · capped launch · transparent job state"
    : pointsMode
      ? "Wallet-bound points · no cash value · no promised token redemption · transparent job state"
      : "Private alpha · no-value test token · experimental runtime · transparent job state");
  set("guarded-settlement-copy", production
    ? `Jobs use ${tokenSymbol()} and an escrow contract on ${chainLabel()}. The token is experimental, volatile, and may lose all value.`
    : pointsMode
      ? "Jobs use off-chain, wallet-bound points. Points cannot be transferred, sold, or redeemed and do not guarantee any future token allocation."
      : networkConfig?.mode === "local-simulation"
      ? "Jobs use local, non-transferable credits while the full queue and verifier lifecycle is exercised without blockchain value."
      : `Jobs use an ERC-20 test token and escrow contract on ${chainLabel()}. ${tokenSymbol()} has no monetary value.`);
  const gasNote = document.getElementById("gas-note");
  if (gasNote && production) gasNote.textContent = `Transactions require ETH for gas on ${chainLabel()}. Rewards use experimental ${tokenSymbol()}.`;
  if (gasNote && pointsMode) gasNote.textContent = "No gas or blockchain transaction is required. The wallet signature is login-only; points cannot be transferred or sold.";
  for (const select of [elements["reward-input"], elements["admin-reward-input"]]) {
    for (const option of Array.from(select?.options || [])) {
      if (production && Number(option.value) > Number(networkConfig?.exposure?.maxRewardCredits || 0)) {
        option.remove();
        continue;
      }
      option.textContent = `${option.value} ${tokenSymbol()}`;
    }
    if (production && select && select.options.length === 0) {
      const cappedReward = Number(networkConfig.exposure.maxRewardCredits);
      select.add(new Option(`${cappedReward} ${tokenSymbol()}`, String(cappedReward), true, true));
    }
    if (select && select.selectedIndex < 0 && select.options.length) select.selectedIndex = 0;
  }
}

async function refreshVerifierStatus() {
  const permissionless = networkConfig?.verification?.permissionless;
  elements["verifier-refresh-button"].disabled = true;
  elements["verifier-refresh-button"].textContent = "Refreshing…";
  try {
    if (!permissionless?.enabled || !permissionless.address) {
      renderVerifierUnavailable(
        "The permissionless registry is staged but intentionally inactive on this legacy settlement deployment.",
        "staged"
      );
      return;
    }
    elements["verifier-registry-address"].textContent = shortId(permissionless.address);
    renderVerifierConfiguration(permissionless.configuration);
    if (!walletSession?.sessionToken) {
      renderVerifierUnavailable("Connect and sign in to inspect this wallet's on-chain verifier identity.", "waiting");
      return;
    }
    const payload = await apiRequest("/api/protocol/verifier/status");
    verifierStatus = payload.verifier;
    renderVerifierConfiguration(payload.registry);
    renderVerifierIdentity(payload.verifier);
    if (payload.verifier.active) {
      const queue = await apiRequest("/api/protocol/verifier/jobs?limit=25");
      renderVerifierJobs(queue.jobs || []);
    } else {
      renderVerifierJobs([]);
    }
  } catch (error) {
    verifierStatus = null;
    renderVerifierUnavailable(getUserFacingError(error, "Verifier status could not be loaded."), "error");
  } finally {
    elements["verifier-refresh-button"].disabled = false;
    elements["verifier-refresh-button"].textContent = "Refresh";
  }
}

function renderVerifierConfiguration(configuration) {
  if (!configuration) return;
  elements["verifier-vote-bond"].textContent = `${formatTokenAmount(configuration.voteBond)} ${tokenSymbol()}`;
  elements["verifier-stake-input"].placeholder = formatTokenAmount(configuration.minimumStake);
}

function renderVerifierIdentity(status) {
  const zeroHash = `0x${"0".repeat(64)}`;
  const registered = Boolean(status.metadataHash && status.metadataHash !== zeroHash);
  const stake = BigInt(status.stake || 0);
  const locked = BigInt(status.locked || 0);
  const pending = BigInt(status.pendingUnstake || 0);
  const available = stake > locked + pending ? stake - locked - pending : 0n;
  const pendingRewards = BigInt(status.pendingRewards || 0);
  const now = Math.floor(Date.now() / 1000);
  elements["verifier-staked"].textContent = `${formatTokenAmount(stake)} ${tokenSymbol()}`;
  elements["verifier-available"].textContent = `${formatTokenAmount(available)} ${tokenSymbol()}`;
  elements["verifier-rewards"].textContent = `${formatTokenAmount(pendingRewards)} ${tokenSymbol()}`;
  elements["verifier-stake-button"].disabled = false;
  elements["verifier-stake-button"].querySelector("span").textContent = registered ? "Add verifier stake" : "Register verifier";
  elements["verifier-unstake-button"].disabled = available <= 0n || pending > 0n;
  elements["verifier-cancel-unstake-button"].disabled = pending <= 0n;
  elements["verifier-withdraw-stake-button"].disabled = pending <= 0n || now < Number(status.unstakeAvailableAt || 0);
  elements["verifier-withdraw-rewards-button"].disabled = pendingRewards <= 0n;

  let heading;
  let detail;
  let state;
  if (status.active) {
    heading = "Verifier active";
    detail = "This wallet can download eligible artifacts and participate in commit/reveal rounds.";
    state = "ready";
    elements["verifier-status-chip"].textContent = "ACTIVE";
    elements["verifier-status-chip"].dataset.tone = "success";
    elements["verifier-next-action"].textContent = "KEEP DAEMON ONLINE";
    setVerifierPhase(2);
  } else if (registered && Number(status.activatedAt || 0) > now) {
    heading = "Activation delay running";
    detail = `Eligible ${new Date(Number(status.activatedAt) * 1000).toLocaleString()}. Keep the dedicated operator ready.`;
    state = "waiting";
    elements["verifier-status-chip"].textContent = "WARMING";
    elements["verifier-status-chip"].dataset.tone = "warning";
    elements["verifier-next-action"].textContent = "WAIT FOR ACTIVATION";
    setVerifierPhase(1);
  } else if (registered) {
    heading = "More available stake required";
    detail = pending > 0n
      ? `An unstake request for ${formatTokenAmount(pending)} ${tokenSymbol()} reduces available stake.`
      : "Add enough stake to meet the registry minimum before this wallet can vote.";
    state = "waiting";
    elements["verifier-status-chip"].textContent = "INACTIVE";
    elements["verifier-status-chip"].dataset.tone = "warning";
    elements["verifier-next-action"].textContent = "RESTORE MINIMUM STAKE";
    setVerifierPhase(1);
  } else {
    heading = "Verifier not registered";
    detail = "Add public operator metadata and stake at least the registry minimum to begin activation.";
    state = "waiting";
    elements["verifier-status-chip"].textContent = "UNREGISTERED";
    elements["verifier-status-chip"].dataset.tone = "neutral";
    elements["verifier-next-action"].textContent = "REGISTER + STAKE";
    setVerifierPhase(0);
  }
  if (pending > 0n) {
    const withdrawalTime = new Date(Number(status.unstakeAvailableAt) * 1000).toLocaleString();
    detail += ` Pending exit unlocks ${withdrawalTime}.`;
  }
  elements["verifier-access-state"].dataset.state = state;
  elements["verifier-access-state"].querySelector("strong").textContent = heading;
  elements["verifier-access-state"].querySelector("p").textContent = detail;
  setVerifierMessage(status.active
    ? "Run the dedicated daemon to review work. No reusable signing authority is stored by this webpage."
    : detail);
}

function renderVerifierUnavailable(message, state = "staged") {
  verifierStatus = null;
  elements["verifier-access-state"].dataset.state = state;
  elements["verifier-access-state"].querySelector("strong").textContent = state === "staged" ? "Protocol staged" : state === "error" ? "Verifier unavailable" : "Wallet required";
  elements["verifier-access-state"].querySelector("p").textContent = message;
  elements["verifier-status-chip"].textContent = state === "staged" ? "STAGED" : state === "error" ? "ERROR" : "CONNECT";
  elements["verifier-status-chip"].dataset.tone = state === "error" ? "error" : state === "waiting" ? "warning" : "neutral";
  elements["verifier-next-action"].textContent = state === "staged" ? "Waiting for the registry" : state === "error" ? "Check the coordinator" : "Connect a wallet";
  elements["verifier-registry-address"].textContent = networkConfig?.verification?.permissionless?.address
    ? shortId(networkConfig.verification.permissionless.address)
    : "NOT ACTIVE";
  for (const id of ["verifier-stake-button", "verifier-unstake-button", "verifier-cancel-unstake-button", "verifier-withdraw-stake-button", "verifier-withdraw-rewards-button"]) {
    elements[id].disabled = true;
  }
  for (const id of ["verifier-staked", "verifier-available", "verifier-rewards"]) elements[id].textContent = "—";
  renderVerifierConfiguration(networkConfig?.verification?.permissionless?.configuration);
  renderVerifierJobs([]);
  setVerifierPhase(0);
  setVerifierMessage(message, state === "error" ? "error" : "neutral");
}

function setVerifierPhase(activeIndex) {
  [...elements["verifier-phase-track"].children].forEach((phase, index) => {
    phase.dataset.state = index < activeIndex ? "complete" : index === activeIndex ? "active" : "idle";
  });
}

function renderVerifierJobs(jobs) {
  elements["verifier-job-count"].textContent = `${jobs.length} ${jobs.length === 1 ? "JOB" : "JOBS"}`;
  elements["verifier-job-list"].replaceChildren();
  if (!jobs.length) {
    const empty = document.createElement("li");
    empty.className = "empty-job";
    empty.textContent = verifierStatus?.active ? "No submitted audits are waiting. The daemon can remain online." : "Activate a verifier to load eligible rounds.";
    elements["verifier-job-list"].append(empty);
    return;
  }
  for (const job of jobs) {
    const item = document.createElement("li");
    item.className = "verifier-job-row";
    const copy = document.createElement("div");
    const title = document.createElement("strong");
    title.textContent = job.prompt || "Submitted inference result";
    const meta = document.createElement("small");
    meta.textContent = `${shortId(job.id)} · ${job.modelId || "pinned model"} · ${rewardLabel(job)}`;
    const action = document.createElement("code");
    action.textContent = "READY TO COMMIT";
    copy.append(title, meta);
    item.append(copy, action);
    elements["verifier-job-list"].append(item);
  }
  elements["verifier-next-action"].textContent = `DAEMON · ${jobs.length} ROUND${jobs.length === 1 ? "" : "S"} READY`;
}

async function manageVerifierStake(event) {
  event.preventDefault();
  await runVerifierTransaction(elements["verifier-stake-button"], "Confirm stake…", async () => {
    const { depositVerifierStake } = await import("./src/verifier-browser-client.js");
    return depositVerifierStake({
      amount: elements["verifier-stake-input"].value,
      metadata: elements["verifier-metadata-input"].value,
      networkConfig,
      eip1193Provider: walletProvider
    });
  }, "Verifier stake confirmed. Activation status refreshed.");
}

async function requestVerifierStakeExit() {
  await runVerifierTransaction(elements["verifier-unstake-button"], "Confirm request…", async () => {
    const { requestVerifierUnstake } = await import("./src/verifier-browser-client.js");
    return requestVerifierUnstake(elements["verifier-stake-input"].value, networkConfig, walletProvider);
  }, "Unstake requested. Funds remain slashable until the exit delay completes.");
}

async function cancelVerifierStakeExit() {
  await runVerifierTransaction(elements["verifier-cancel-unstake-button"], "Confirm cancel…", async () => {
    const { cancelVerifierUnstake } = await import("./src/verifier-browser-client.js");
    return cancelVerifierUnstake(networkConfig, walletProvider);
  }, "Pending unstake cancelled.");
}

async function withdrawVerifierStakeExit() {
  await runVerifierTransaction(elements["verifier-withdraw-stake-button"], "Confirm withdrawal…", async () => {
    const { withdrawVerifierStake } = await import("./src/verifier-browser-client.js");
    return withdrawVerifierStake(networkConfig, walletProvider);
  }, "Unlocked verifier stake withdrawn.");
}

async function withdrawVerifierEarnings() {
  await runVerifierTransaction(elements["verifier-withdraw-rewards-button"], "Confirm withdrawal…", async () => {
    const { withdrawVerifierRewards } = await import("./src/verifier-browser-client.js");
    return withdrawVerifierRewards(networkConfig, walletProvider);
  }, "Verifier rewards withdrawn.");
}

async function runVerifierTransaction(button, busyLabel, operation, successMessage) {
  const label = button.querySelector("span") || button;
  const idleLabel = label.textContent;
  let failed = false;
  button.disabled = true;
  label.textContent = busyLabel;
  setVerifierMessage("Check the wallet transaction details before approving.");
  try {
    await ensureRobinhoodTestnet();
    const hash = await operation();
    await refreshVerifierStatus();
    setVerifierMessage(`${successMessage} Transaction ${shortId(hash)}.`, "success");
  } catch (error) {
    failed = true;
    setVerifierMessage(getUserFacingError(error), "error");
  } finally {
    if (label === button || failed) label.textContent = idleLabel;
    if (failed && verifierStatus) {
      const message = elements["verifier-form-message"].textContent;
      renderVerifierIdentity(verifierStatus);
      setVerifierMessage(message, "error");
    }
  }
}

function setVerifierMessage(message, tone = "neutral") {
  elements["verifier-form-message"].textContent = message;
  elements["verifier-form-message"].dataset.tone = tone;
}

async function refreshAdminAccess() {
  if (!networkConfig?.admin?.enabled) {
    renderAdminLocked("Admin controls are not configured on this deployment.", "unavailable");
    return;
  }
  if (!walletSession?.sessionToken) {
    renderAdminLocked("Connect and sign in with the configured operator wallet.");
    return;
  }
  elements["admin-refresh-button"].disabled = true;
  elements["admin-refresh-button"].textContent = "Refreshing…";
  try {
    const overview = await apiRequest("/api/admin/overview");
    adminAuthorized = true;
    document.querySelectorAll("[data-admin-link]").forEach((link) => { link.hidden = false; });
    elements["admin-session-chip"].hidden = false;
    elements["admin-create-job-button"].disabled = false;
    elements["admin-access-state"].dataset.state = "ready";
    elements["admin-access-state"].innerHTML = '<span class="gate-node" aria-hidden="true"></span><div><strong>Operator authenticated</strong><p>This wallet can publish network-wide bounties through guarded escrow.</p></div>';
    renderAdminOverview(overview);
  } catch (error) {
    if ([401, 403].includes(error.status)) renderAdminLocked("The connected wallet is not the configured network operator.");
    else if (adminAuthorized) renderAdminRefreshError(getUserFacingError(error));
    else renderAdminLocked(getUserFacingError(error), "error");
  } finally {
    elements["admin-refresh-button"].disabled = false;
    elements["admin-refresh-button"].textContent = "Refresh";
  }
}

function renderAdminRefreshError(message) {
  document.querySelectorAll("[data-admin-link]").forEach((link) => { link.hidden = false; });
  elements["admin-session-chip"].hidden = false;
  elements["admin-create-job-button"].disabled = false;
  elements["admin-access-state"].dataset.state = "error";
  elements["admin-access-state"].innerHTML = `<span class="gate-node" aria-hidden="true"></span><div><strong>Operator still authenticated</strong><p>Network overview could not refresh: ${escapeHtml(message)}</p></div>`;
}

function renderAdminLocked(message, state = "locked") {
  adminAuthorized = false;
  document.querySelectorAll("[data-admin-link]").forEach((link) => { link.hidden = true; });
  elements["admin-session-chip"].hidden = true;
  elements["admin-create-job-button"].disabled = true;
  elements["admin-access-state"].dataset.state = state;
  elements["admin-access-state"].innerHTML = `<span class="gate-node" aria-hidden="true"></span><div><strong>Operator wallet required</strong><p>${escapeHtml(message)}</p></div>`;
  elements["admin-open-jobs"].textContent = "—";
  elements["admin-accepted-jobs"].textContent = "—";
  elements["admin-pending-settlement"].textContent = "—";
  elements["admin-verifier-health"].textContent = "—";
  elements["admin-job-list"].innerHTML = '<li class="empty-job">Authenticate the operator wallet to load global jobs.</li>';
}

function renderAdminOverview(overview) {
  const states = overview.metrics?.jobsByState || {};
  const active = ["awaiting_funding", "queued", "claimed", "submitted"].reduce((sum, state) => sum + Number(states[state] || 0), 0);
  elements["admin-open-jobs"].textContent = active;
  elements["admin-accepted-jobs"].textContent = Number(states.accepted || 0);
  elements["admin-pending-settlement"].textContent = Number(overview.metrics?.chainSettlementPending || 0);
  const verifierHealth = overview.metrics?.verifierHealth;
  elements["admin-verifier-health"].textContent = verifierHealth
    ? `${Number(verifierHealth.online || 0)}/${Number(verifierHealth.configured || 0)}`
    : "—";
  elements["admin-verifier-health"].dataset.tone = verifierHealth?.online >= verifierHealth?.configured ? "success" : "warning";
  elements["admin-job-list"].replaceChildren();
  if (!overview.globalJobs?.length) {
    elements["admin-job-list"].innerHTML = '<li class="empty-job">No global bounties published yet.</li>';
    return;
  }
  for (const job of overview.globalJobs) {
    const item = document.createElement("li");
    item.className = "admin-job-row";
    const copy = document.createElement("div");
    const prompt = document.createElement("strong");
    prompt.textContent = job.prompt;
    const meta = document.createElement("span");
    meta.textContent = `${rewardLabel(job)} · ${shortId(job.id)} · ${new Date(job.createdAt).toLocaleDateString()}`;
    copy.append(prompt, meta);
    const state = document.createElement("code");
    state.textContent = job.state.replaceAll("_", " ");
    state.dataset.tone = ["expired", "rejected", "awaiting_funding"].includes(job.state) ? "warning" : "success";
    item.append(copy, state);
    elements["admin-job-list"].append(item);
  }
}

async function createGlobalJob(event) {
  event.preventDefault();
  if (!adminAuthorized) return;
  const button = elements["admin-create-job-button"];
  const label = button.querySelector("span");
  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  label.textContent = "Preparing escrow…";
  setAdminMessage("Creating the global job; your wallet will confirm its bounty…");
  try {
    const job = await apiRequest("/api/admin/jobs", {
      method: "POST",
      body: JSON.stringify({
        prompt: elements["admin-prompt-input"].value,
        rewardCredits: Number(elements["admin-reward-input"].value),
        width: 256,
        height: 256,
        steps: 4
      })
    });
    saveConsumerToken(job.id, job.consumerToken);
    if (job.settlementMode === "onchain") await fundOnchainJob(job);
    elements["admin-prompt-input"].value = "";
    setAdminMessage(`Global bounty ${shortId(job.id)} is live for every qualified device.`, "success");
    await Promise.all([refreshAdminAccess(), refreshQueue(), refreshBalance()]);
  } catch (error) {
    setAdminMessage(getUserFacingError(error), "error");
  } finally {
    button.disabled = !adminAuthorized;
    button.removeAttribute("aria-busy");
    label.textContent = "Fund global bounty";
  }
}

function setAdminMessage(message, tone = "neutral") {
  elements["admin-form-message"].textContent = message;
  elements["admin-form-message"].dataset.tone = tone;
}

async function fundOnchainJob(job) {
  if (!networkConfig?.settlement?.enabled) throw new Error("Testnet escrow is not configured.");
  let transactionHash = getFundingTransaction(job.id);
  if (!transactionHash) {
    const recovered = await recoverOnchainFunding(job, { optional: true });
    if (recovered) return recovered;
  }
  if (!transactionHash) {
    const { createEscrowJob } = await import("./src/escrow-browser-client.js");
    try {
      transactionHash = await createEscrowJob(job, networkConfig, walletProvider);
    } catch (error) {
      const recovered = await recoverOnchainFunding(job, { optional: true });
      if (recovered) return recovered;
      throw error;
    }
    saveFundingTransaction(job.id, transactionHash);
  }
  const funded = await apiRequest(`/api/jobs/${encodeURIComponent(job.id)}/funding`, {
    method: "POST",
    body: JSON.stringify({ consumerId: participantId, transactionHash })
  });
  saveFundingTransaction(job.id, null);
  return funded;
}

async function recoverOnchainFunding(job, { optional = false } = {}) {
  try {
    const recovered = await apiRequest(`/api/jobs/${encodeURIComponent(job.id)}/funding-recovery`, {
      method: "POST",
      body: JSON.stringify({ consumerId: participantId })
    });
    saveFundingTransaction(job.id, null);
    setFormMessage(`Recovered existing escrow for ${shortId(job.id)}; the render is now shared in the queue.`, "success");
    return recovered;
  } catch (error) {
    if (optional && error.code === "funding_not_found") return null;
    throw error;
  }
}

function rewardLabel(job) {
  return job.settlementMode === "onchain"
    ? `${formatTokenAmount(job.rewardWei)} ${tokenSymbol()}`
    : `${job.rewardCredits} ${networkConfig?.mode === "points-alpha" ? tokenSymbol() : "cr"}`;
}

async function createRenderJob(event) {
  event.preventDefault();
  elements["create-job-button"].disabled = true;
  elements["create-job-button"].setAttribute("aria-busy", "true");
  const createLabel = elements["create-job-button"].querySelector("span");
  const idleCreateLabel = createLabel?.textContent;
  if (createLabel) createLabel.textContent = "Preparing escrow…";
  setFormMessage(networkConfig?.settlement?.enabled
    ? `Creating the coordinator draft; your wallet will confirm ${chainLabel()} escrow funding…`
    : networkConfig?.mode === "points-alpha" ? "Holding points and creating job…" : "Escrowing credits and creating job…");
  try {
    const job = await apiRequest("/api/jobs", {
      method: "POST",
      body: JSON.stringify({
        consumerId: participantId,
        prompt: elements["prompt-input"].value,
        rewardCredits: Number(elements["reward-input"].value),
        width: 256,
        height: 256,
        steps: 4
      })
    });
    saveConsumerToken(job.id, job.consumerToken);
    if (job.settlementMode === "onchain") {
      await fundOnchainJob(job);
      setFormMessage(`Job ${shortId(job.id)} is funded on ${chainLabel()} and queued.`, "success");
    } else {
      setFormMessage(`Job ${shortId(job.id)} is queued. ${networkConfig?.mode === "points-alpha" ? "Points" : "Credits"} are held until acceptance or expiry.`, "success");
    }
    await Promise.all([refreshBalance(), refreshQueue(), refreshConsumerResults()]);
  } catch (error) {
    setFormMessage(getUserFacingError(error), "error");
  } finally {
    elements["create-job-button"].disabled = false;
    elements["create-job-button"].removeAttribute("aria-busy");
    if (createLabel && idleCreateLabel) createLabel.textContent = idleCreateLabel;
  }
}

async function refreshQueue() {
  const refreshButton = elements["refresh-queue-button"];
  const idleLabel = refreshButton.textContent;
  refreshButton.disabled = true;
  refreshButton.setAttribute("aria-busy", "true");
  refreshButton.textContent = "Refreshing…";
  try {
    const { jobs } = await apiRequest("/api/jobs?state=queued&limit=25");
    renderQueueJobs(jobs);
  } catch (error) {
    elements["job-queue"].innerHTML = `<li class="empty-job">${escapeHtml(getUserFacingError(error, "The queue could not be loaded."))}</li>`;
  } finally {
    refreshButton.disabled = false;
    refreshButton.removeAttribute("aria-busy");
    refreshButton.textContent = idleLabel;
  }
}

function renderQueueJobs(jobs) {
  elements["queue-count"].textContent = jobs.length;
  elements["job-queue"].replaceChildren();
  if (!jobs.length) {
    const empty = document.createElement("li");
    empty.className = "empty-job";
    empty.textContent = autoWorkerRequested ? "Queue clear. Auto worker is watching for the next packet." : "No queued work yet.";
    elements["job-queue"].append(empty);
  } else {
    for (const job of jobs) elements["job-queue"].append(renderJobCard(job));
  }
  renderQualificationGate();
}

function renderJobCard(job) {
  const item = document.createElement("li");
  item.className = "job-card";
  const detail = document.createElement("div");
  const prompt = document.createElement("p");
  prompt.textContent = job.prompt;
  const meta = document.createElement("div");
  meta.className = "job-card-meta";
  meta.innerHTML = `<span>${job.width}²</span><span>${job.steps} steps</span><span>${shortId(job.id)}</span>`;
  if (job.scope === "global") {
    const globalBadge = document.createElement("span");
    globalBadge.className = "global-bounty-badge";
    globalBadge.textContent = "GLOBAL";
    meta.prepend(globalBadge);
  }
  detail.append(prompt, meta);

  const side = document.createElement("div");
  side.className = "job-card-side";
  const reward = document.createElement("strong");
  reward.textContent = rewardLabel(job);
  const claim = document.createElement("button");
  claim.className = "small-button";
  claim.type = "button";
  const eligibility = contributionEligibility(job);
  const autoWatching = autoWorkerRequested && !activeWorkerJobId;
  claim.textContent = autoWatching ? "Auto watching" : eligibility.buttonLabel;
  claim.disabled = autoWatching || !eligibility.allowed;
  claim.title = autoWatching ? "The auto worker owns queue selection while it is active." : eligibility.detail;
  claim.setAttribute("aria-label", `${claim.textContent}: ${claim.title}`);
  claim.addEventListener("click", () => claimJob(job.id, claim));
  side.append(reward, claim);
  item.append(detail, side);
  return item;
}

async function claimJob(jobId, button = null) {
  if (!contributorQualification?.grantToken) return false;
  if (button) button.disabled = true;
  try {
    const claim = await apiRequest(`/api/jobs/${encodeURIComponent(jobId)}/claim`, {
      method: "POST",
      body: JSON.stringify({ workerId: participantId, qualificationToken: contributorQualification?.grantToken })
    });
    activeWorkerJobId = jobId;
    if (claim.job.settlementMode === "onchain") {
      const session = activeWorkerSession();
      if (networkConfig?.settlement?.sessionRelayEnabled && autoWorkerRequested && !session) {
        throw new Error("The unattended worker shift expired before this claim. Authorize a new shift to continue.");
      }
      if (networkConfig?.settlement?.sessionRelayEnabled && session) {
        setWorkerStatus("active", "Relaying session claim", `${shortId(jobId)} · bounded key · no wallet popup`);
        const payload = await apiRequest(`/api/jobs/${encodeURIComponent(jobId)}/session-claim-payload`, {
          method: "POST",
          body: JSON.stringify({
            workerId: participantId,
            grant: session.grant,
            workerAuthorization: session.workerAuthorization
          })
        });
        const sessionSignature = await signWorkerSessionPayload(session, payload);
        await apiRequest(`/api/jobs/${encodeURIComponent(jobId)}/session-claim`, {
          method: "POST",
          body: JSON.stringify({
            workerId: participantId,
            grant: session.grant,
            workerAuthorization: session.workerAuthorization,
            claimAuthorization: payload.claimAuthorization,
            sessionSignature
          })
        });
      } else {
        setWorkerStatus("active", "Authorizing on-chain claim", `${shortId(jobId)} · waiting for wallet confirmation`);
        const authorization = await apiRequest(`/api/jobs/${encodeURIComponent(jobId)}/claim-authorization`, {
          method: "POST",
          body: JSON.stringify({ workerId: participantId })
        });
        const { claimEscrowJob } = await import("./src/escrow-browser-client.js");
        const transactionHash = await claimEscrowJob(authorization, networkConfig, walletProvider);
        await apiRequest(`/api/jobs/${encodeURIComponent(jobId)}/chain-claim`, {
          method: "POST",
          body: JSON.stringify({ workerId: participantId, transactionHash })
        });
      }
    }
    await refreshQueue();
    if (autoWorkerRequested) {
      autoWorkerState = "rendering";
      autoWorkerDetail = `${shortId(jobId)} claimed. WebGPU inference is running locally.`;
      renderAutoWorkerControl();
    }
    await executeClaimedJob(claim.job, claim.claimToken);
    if (autoWorkerRequested) {
      autoWorkerState = "verifying";
      autoWorkerDetail = networkConfig?.settlement?.bonding
        ? `${shortId(jobId)} committed. Waiting for audit selection and the dispute window.`
        : `${shortId(jobId)} uploaded. Waiting for independent verifier quorum.`;
      renderAutoWorkerControl();
    }
    setFormMessage(networkConfig?.settlement?.bonding
      ? `Submitted ${shortId(jobId)} to bonded settlement.`
      : `Submitted ${shortId(jobId)} for verifier quorum.`, "success");
    activeWorkerJobId = null;
    await Promise.all([refreshQueue(), refreshBalance(), refreshConsumerResults(), refreshWorkerHistory()]);
    return true;
  } catch (error) {
    activeWorkerJobId = null;
    setWorkerStatus("error", "Worker stopped", getUserFacingError(error));
    setFormMessage(getUserFacingError(error), "error");
    await Promise.allSettled([refreshQueue(), refreshWorkerHistory()]);
    if (button) button.disabled = false;
    return false;
  }
}

async function executeClaimedJob(job, claimToken) {
  const adapter = globalThis.SHAREMYAI_FLUX_ADAPTER;
  if (!adapter?.render) throw new Error("The local model runtime is not ready.");
  const controller = beginAbortableOperation();
  setWorkerStatus("active", "Rendering claimed job", `${shortId(job.id)} · starting text encoder`);
  let heartbeatError = null;
  const heartbeat = window.setInterval(async () => {
    try {
      await apiRequest(`/api/jobs/${encodeURIComponent(job.id)}/heartbeat`, {
        method: "POST",
        body: JSON.stringify({ claimToken })
      });
    } catch (error) {
      heartbeatError = error;
      logEvent(`Lease heartbeat failed for ${shortId(job.id)}: ${getUserFacingError(error)}`);
    }
  }, 60_000);

  try {
    const startedAt = performance.now();
    const output = await adapter.render({
      prompt: job.prompt,
      negativePrompt: job.negativePrompt,
      seed: job.seed,
      width: job.width,
      height: job.height,
      steps: job.steps,
      guidance: 1,
      signal: controller.signal,
      onProgress(progress) {
        const message = progress?.message || "Rendering claimed job.";
        setWorkerStatus("active", "Rendering claimed job", message);
      }
    });
    if (heartbeatError) throw new Error(`The render completed after its lease became unhealthy: ${heartbeatError.message}`);
    const renderMs = Math.max(1, Math.round(performance.now() - startedAt));
    if (job.settlementMode === "onchain") {
      setWorkerStatus("active", `Committing output on ${chainLabel()}`, `sha256:${output.outputHash}`);
      const session = activeWorkerSession();
      if (networkConfig?.settlement?.sessionRelayEnabled && autoWorkerRequested && !session) {
        throw new Error("The unattended worker shift expired during rendering. Use the wallet fallback to commit this result.");
      }
      if (networkConfig?.settlement?.sessionRelayEnabled && session) {
        const payload = await apiRequest(`/api/jobs/${encodeURIComponent(job.id)}/session-submission-payload`, {
          method: "POST",
          body: JSON.stringify({
            workerId: participantId,
            outputHash: output.outputHash,
            grant: session.grant,
            workerAuthorization: session.workerAuthorization
          })
        });
        const sessionSignature = await signWorkerSessionPayload(session, payload);
        await apiRequest(`/api/jobs/${encodeURIComponent(job.id)}/session-submission`, {
          method: "POST",
          body: JSON.stringify({
            workerId: participantId,
            outputHash: output.outputHash,
            grant: session.grant,
            workerAuthorization: session.workerAuthorization,
            sessionSignature
          })
        });
      } else {
        const { submitEscrowResult } = await import("./src/escrow-browser-client.js");
        const transactionHash = await submitEscrowResult(job.chainJobId, output.outputHash, networkConfig, walletProvider);
        await apiRequest(`/api/jobs/${encodeURIComponent(job.id)}/chain-submission`, {
          method: "POST",
          body: JSON.stringify({ workerId: participantId, transactionHash, outputHash: output.outputHash })
        });
      }
    }
    setWorkerStatus("active", "Uploading output", `${formatBytes(output.image.size)} PNG · server will recompute SHA-256`);
    const response = await fetch(`/api/jobs/${encodeURIComponent(job.id)}/artifact`, {
      method: "POST",
      headers: {
        "content-type": "image/png",
        "x-claim-token": claimToken,
        "x-render-ms": String(renderMs)
      },
      body: output.image
    });
    const submitted = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(submitted.error?.message || `Artifact upload returned HTTP ${response.status}.`);
    if (submitted.outputHash !== output.outputHash) throw new Error("Server and browser output hashes disagree.");
    setWorkerStatus(
      "complete",
      "Output submitted",
      networkConfig?.settlement?.bonding
        ? `${shortId(job.id)} · committed to bonded settlement`
        : `${shortId(job.id)} · awaiting 2 verifier votes`
    );
    logEvent(`Job ${shortId(job.id)} uploaded: sha256:${submitted.outputHash}.`);
  } finally {
    window.clearInterval(heartbeat);
    endAbortableOperation(controller);
  }
}

function setWorkerStatus(state, title, detail) {
  elements["worker-status"].dataset.state = state;
  elements["worker-status"].innerHTML = `<span class="gate-node" aria-hidden="true"></span><div><strong>${escapeHtml(title)}</strong><p>${escapeHtml(detail)}</p></div>`;
}

async function refreshConsumerResults() {
  const refreshButton = elements["refresh-results-button"];
  const idleLabel = refreshButton.textContent;
  refreshButton.disabled = true;
  refreshButton.setAttribute("aria-busy", "true");
  refreshButton.textContent = "Refreshing…";
  try {
    const { jobs } = await apiRequest(`/api/jobs?consumerId=${encodeURIComponent(participantId)}&limit=25`);
    elements["consumer-results"].replaceChildren();
    if (!jobs.length) {
      elements["consumer-results"].innerHTML = '<li class="empty-job">No requests from this identity yet.</li>';
      return;
    }
    const cards = await Promise.all(jobs.map((job) => renderConsumerResult(job)));
    elements["consumer-results"].append(...cards);
  } catch (error) {
    elements["consumer-results"].innerHTML = `<li class="empty-job">${escapeHtml(getUserFacingError(error, "Results could not be loaded."))}</li>`;
  } finally {
    refreshButton.disabled = false;
    refreshButton.removeAttribute("aria-busy");
    refreshButton.textContent = idleLabel;
  }
}

async function refreshWorkerHistory() {
  const refreshButton = elements["refresh-worker-history-button"];
  const idleLabel = refreshButton.textContent;
  if (networkConfig?.authentication?.walletSessionRequired && !walletSession?.sessionToken) {
    elements["worker-history-count"].textContent = "—";
    elements["worker-history"].innerHTML = '<li class="empty-job">Connect and authenticate a wallet to load its contributor history.</li>';
    return;
  }
  refreshButton.disabled = true;
  refreshButton.setAttribute("aria-busy", "true");
  refreshButton.textContent = "Refreshing…";
  try {
    const { jobs } = await apiRequest(`/api/jobs?workerId=${encodeURIComponent(participantId)}&limit=50`);
    updateAutoWorkerEarnings(jobs);
    elements["worker-history-count"].textContent = jobs.length;
    elements["worker-history"].replaceChildren();
    if (!jobs.length) {
      elements["worker-history"].innerHTML = '<li class="empty-job">No claimed work for this wallet yet.</li>';
      return;
    }
    elements["worker-history"].append(...jobs.map(renderWorkerJob));
  } catch (error) {
    elements["worker-history"].innerHTML = `<li class="empty-job">${escapeHtml(getUserFacingError(error, "Contributor history could not be loaded."))}</li>`;
  } finally {
    refreshButton.disabled = false;
    refreshButton.removeAttribute("aria-busy");
    refreshButton.textContent = idleLabel;
  }
}

function toggleAutoWorker() {
  if (autoWorkerRequested) stopAutoWorker();
  else void startAutoWorker();
}

async function toggleWorkerSession() {
  if (workerSessionInFlight) return;
  if (activeWorkerSession()) {
    stopAutoWorker("Auto-work stopped before ending this authorized shift.");
    workerSessionInFlight = true;
    renderAutoWorkerControl();
    try {
      await revokeWorkerSession(networkConfig, walletProvider, workerSession);
      workerSession = null;
      autoWorkerState = "idle";
      autoWorkerDetail = `Worker shift ended and revoked on ${chainLabel()}.`;
    } catch (error) {
      autoWorkerState = "error";
      autoWorkerDetail = getUserFacingError(error, "The worker shift could not be revoked.");
    } finally {
      workerSessionInFlight = false;
      renderAutoWorkerControl();
    }
    return;
  }
  await authorizeCurrentWorkerSession();
}

async function authorizeCurrentWorkerSession() {
  if (activeWorkerSession()) return workerSession;
  if (!networkConfig?.settlement?.sessionRelayEnabled) return null;
  if (!walletSession?.sessionToken || !walletProvider?.request) {
    throw new Error("Connect and authenticate the worker wallet before authorizing a shift.");
  }
  workerSessionInFlight = true;
  autoWorkerState = "starting";
  autoWorkerDetail = "Authorize one bounded worker shift in your wallet. No transaction is sent.";
  renderAutoWorkerControl();
  try {
    workerSession = await authorizeWorkerSession(networkConfig, walletProvider, participantId);
    if (!autoWorkerRequested) autoWorkerState = "idle";
    autoWorkerDetail = "Shift authorized. Claims and result commits can now run without wallet popups.";
    return workerSession;
  } catch (error) {
    workerSession = null;
    autoWorkerState = "error";
    autoWorkerDetail = getUserFacingError(error, "The worker shift could not be authorized.");
    throw error;
  } finally {
    workerSessionInFlight = false;
    renderAutoWorkerControl();
  }
}

function activeWorkerSession() {
  if (!workerSession?.signer || !workerSession?.expiresAt) return null;
  if (Date.parse(workerSession.expiresAt) <= Date.now()) {
    workerSession = null;
    return null;
  }
  if (workerSession.worker.toLowerCase() !== String(participantId || "").toLowerCase()) return null;
  return workerSession;
}

async function startAutoWorker() {
  const eligibility = contributionEligibility();
  if (!eligibility.allowed) {
    autoWorkerState = "blocked";
    autoWorkerDetail = eligibility.detail;
    renderAutoWorkerControl();
    return;
  }
  if (networkConfig?.settlement?.sessionRelayEnabled && !activeWorkerSession()) {
    try { await authorizeCurrentWorkerSession(); }
    catch { return; }
  }
  if (!navigator.locks?.request) {
    autoWorkerState = "error";
    autoWorkerDetail = "This browser cannot guarantee that only one ShareMyAI tab will claim work. Use a current Chromium browser.";
    renderAutoWorkerControl();
    return;
  }
  autoWorkerRequested = true;
  void acquireAutoWorkerWakeLock();
  autoWorkerState = "starting";
  autoWorkerDetail = "Securing the single-tab worker lock…";
  renderAutoWorkerControl();
  void navigator.locks.request("sharemyai:auto-worker:v1", { mode: "exclusive", ifAvailable: true }, async (lock) => {
    if (!lock) {
      autoWorkerRequested = false;
      void releaseAutoWorkerWakeLock();
      autoWorkerState = "error";
      autoWorkerDetail = "Auto-work is already running in another ShareMyAI tab for this browser profile.";
      renderAutoWorkerControl();
      return;
    }
    if (!autoWorkerRequested) return;
    autoWorkerLockHeld = true;
    autoWorkerState = "watching";
    autoWorkerDetail = "Watching the queue. The oldest eligible packet will be claimed first.";
    renderAutoWorkerControl();
    scheduleAutoWorker(0);
    await new Promise((resolve) => { autoWorkerLockRelease = resolve; });
    autoWorkerLockRelease = null;
    autoWorkerLockHeld = false;
    renderAutoWorkerControl();
  }).catch((error) => {
    autoWorkerRequested = false;
    void releaseAutoWorkerWakeLock();
    autoWorkerLockHeld = false;
    autoWorkerState = "error";
    autoWorkerDetail = getUserFacingError(error, "The auto worker could not start.");
    renderAutoWorkerControl();
  });
}

function stopAutoWorker(reason = null) {
  if (!autoWorkerRequested && !autoWorkerLockHeld) return;
  autoWorkerRequested = false;
  if (autoWorkerTimer) window.clearTimeout(autoWorkerTimer);
  autoWorkerTimer = null;
  void releaseAutoWorkerWakeLock();
  if (autoWorkerTickInFlight || activeWorkerJobId) {
    autoWorkerState = "stopping";
    autoWorkerDetail = reason || "Stopping after the current packet reaches a safe point.";
  } else {
    autoWorkerState = "idle";
    autoWorkerDetail = reason || "Auto-work is off. Start it when this device is ready to earn.";
    releaseAutoWorkerLock();
  }
  renderAutoWorkerControl();
  void refreshQueue();
}

async function acquireAutoWorkerWakeLock() {
  if (!autoWorkerRequested || autoWorkerWakeLock || document.visibilityState !== "visible" || !navigator.wakeLock?.request) return;
  try {
    autoWorkerWakeLock = await navigator.wakeLock.request("screen");
    autoWorkerWakeLock.addEventListener("release", () => {
      autoWorkerWakeLock = null;
      renderAutoWorkerControl();
    }, { once: true });
    renderAutoWorkerControl();
  } catch {
    autoWorkerWakeLock = null;
  }
}

async function releaseAutoWorkerWakeLock() {
  const lock = autoWorkerWakeLock;
  autoWorkerWakeLock = null;
  if (lock) await lock.release().catch(() => {});
}

function releaseAutoWorkerLock() {
  const release = autoWorkerLockRelease;
  autoWorkerLockRelease = null;
  autoWorkerLockHeld = false;
  if (release) release();
}

function scheduleAutoWorker(delay) {
  if (!autoWorkerRequested || !autoWorkerLockHeld) return;
  if (autoWorkerTimer) window.clearTimeout(autoWorkerTimer);
  autoWorkerTimer = window.setTimeout(() => void runAutoWorkerTick(), delay);
}

async function runAutoWorkerTick() {
  if (!autoWorkerRequested || !autoWorkerLockHeld || autoWorkerTickInFlight) return;
  autoWorkerTickInFlight = true;
  let outcome = "idle";
  try {
    if (!navigator.onLine) {
      outcome = "failed";
      autoWorkerConsecutiveFailures += 1;
      autoWorkerState = "error";
      autoWorkerDetail = "Network offline. Auto-work will retry with backoff.";
      return;
    }
    const eligibility = contributionEligibility();
    if (!eligibility.allowed) {
      outcome = "blocked";
      autoWorkerState = "blocked";
      autoWorkerDetail = eligibility.detail;
      return;
    }
    autoWorkerLastScanAt = new Date();
    autoWorkerState = "scanning";
    autoWorkerDetail = "Scanning for an eligible queue packet…";
    renderAutoWorkerControl();
    const { jobs } = await apiRequest("/api/jobs?state=queued&limit=25");
    renderQueueJobs(jobs);
    const job = chooseAutoWorkerJob(jobs, {
      participantId,
      isEligible: (candidate) => contributionEligibility(candidate).allowed
    });
    if (!job) {
      autoWorkerState = "watching";
      autoWorkerDetail = "Queue clear. Watching for the next eligible packet.";
      autoWorkerConsecutiveFailures = 0;
      await refreshWorkerHistory();
      if (Date.now() - autoWorkerLastBalanceRefreshAt >= 30_000) {
        await refreshBalance();
        autoWorkerLastBalanceRefreshAt = Date.now();
      }
      return;
    }
    autoWorkerState = "claiming";
    autoWorkerDetail = networkConfig?.settlement?.sessionRelayEnabled && activeWorkerSession()
      ? `${shortId(job.id)} selected. The bounded session key is relaying its claim.`
      : networkConfig?.settlement?.enabled
      ? `${shortId(job.id)} selected. Your wallet will request claim and result confirmations.`
      : `${shortId(job.id)} selected. Claiming and rendering automatically.`;
    renderAutoWorkerControl();
    const submitted = await claimJob(job.id);
    if (!submitted) {
      outcome = "failed";
      autoWorkerConsecutiveFailures += 1;
      autoWorkerState = "error";
      autoWorkerDetail = "The packet did not complete. Auto-work will retry after a safety backoff.";
      return;
    }
    outcome = "submitted";
    autoWorkerConsecutiveFailures = 0;
    autoWorkerSubmittedCount += 1;
    autoWorkerSessionJobIds.add(job.id);
    autoWorkerLastBalanceRefreshAt = 0;
    autoWorkerState = "watching";
    autoWorkerDetail = `${shortId(job.id)} submitted. Watching for the next packet while verifiers review it.`;
  } catch (error) {
    outcome = "failed";
    autoWorkerConsecutiveFailures += 1;
    autoWorkerState = "error";
    autoWorkerDetail = getUserFacingError(error, "Auto-work hit a recoverable coordinator error.");
  } finally {
    autoWorkerTickInFlight = false;
    renderAutoWorkerControl();
    if (!autoWorkerRequested) {
      autoWorkerState = "idle";
      releaseAutoWorkerLock();
      renderAutoWorkerControl();
    } else {
      scheduleAutoWorker(getAutoWorkerDelay(outcome, autoWorkerConsecutiveFailures));
    }
  }
}

function updateAutoWorkerEarnings(jobs) {
  const sessionJobs = jobs.filter((job) => autoWorkerSessionJobIds.has(job.id));
  const accepted = sessionJobs.filter((job) => job.state === "accepted");
  autoWorkerAcceptedCount = accepted.length;
  if (networkConfig?.settlement?.enabled) {
    const total = accepted.reduce((sum, job) => sum + BigInt(job.rewardWei || "0"), 0n);
    autoWorkerEarnedLabel = `${formatTokenAmount(total)} ${tokenSymbol()}`;
  } else {
    autoWorkerEarnedLabel = `${accepted.reduce((sum, job) => sum + Number(job.rewardCredits || 0), 0)} ${networkConfig?.mode === "points-alpha" ? tokenSymbol() : "cr"}`;
  }
  renderAutoWorkerControl();
}

function renderAutoWorkerControl() {
  const eligibility = contributionEligibility();
  const button = elements["auto-worker-button"];
  const sessionButton = elements["session-key-button"];
  const session = activeWorkerSession();
  elements["auto-worker-console"].dataset.state = autoWorkerState;
  elements["auto-worker-state"].textContent = ({
    idle: "Off", starting: "STARTING", watching: "WATCHING", scanning: "SCANNING",
    claiming: "CLAIMING", rendering: "RENDERING", verifying: "VERIFYING",
    stopping: "STOPPING", blocked: "BLOCKED", error: "RETRYING"
  })[autoWorkerState] || autoWorkerState;
  elements["auto-worker-detail"].textContent = autoWorkerDetail;
  elements["auto-worker-submitted"].textContent = autoWorkerSubmittedCount;
  elements["auto-worker-accepted"].textContent = autoWorkerAcceptedCount;
  elements["auto-worker-earned"].textContent = autoWorkerEarnedLabel;
  elements["auto-worker-last-scan"].textContent = autoWorkerLastScanAt
    ? autoWorkerLastScanAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : "—";
  button.setAttribute("aria-pressed", String(autoWorkerRequested));
  if (autoWorkerRequested) {
    button.textContent = activeWorkerJobId || autoWorkerTickInFlight ? "Stop after job" : "Stop auto-work";
    button.disabled = false;
  } else {
    button.textContent = eligibility.allowed ? "Start auto-work" : "Setup required";
    button.disabled = !eligibility.allowed;
  }
  sessionButton.hidden = !networkConfig?.settlement?.sessionRelayEnabled;
  sessionButton.disabled = workerSessionInFlight || !walletSession?.sessionToken;
  sessionButton.textContent = workerSessionInFlight
    ? "Authorizing…"
    : session
      ? `End shift · ${formatSessionRemaining(session.expiresAt)}`
      : "Authorize shift";
  elements["auto-worker-note"].textContent = networkConfig?.settlement?.sessionRelayEnabled
    ? session
      ? `Unattended shift active until ${new Date(session.expiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}. The in-memory key can only claim and submit up to ${session.grant.maxClaims} jobs; it cannot transfer tokens or withdraw rewards.${autoWorkerWakeLock ? " Screen wake lock active." : ""}`
      : "Authorize one bounded shift to remove per-job wallet popups. The session key stays in this tab's memory and disappears when the tab closes."
    : networkConfig?.settlement?.enabled
    ? `Wallet-assisted alpha: queue, render, upload, and retries are automatic; the wallet still confirms claim and result transactions.${autoWorkerWakeLock ? " Screen wake lock active." : ""}`
    : `One-tab lock active. Auto-work processes one job at a time and stops when this tab closes.${autoWorkerWakeLock ? " Screen wake lock active." : ""}`;
}

function formatSessionRemaining(expiresAt) {
  const minutes = Math.max(0, Math.ceil((Date.parse(expiresAt) - Date.now()) / 60_000));
  const hours = Math.floor(minutes / 60);
  return hours ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
}

function renderWorkerJob(job) {
  const presentation = getWorkerJobPresentation(job);
  const item = document.createElement("li");
  item.className = "worker-job";

  const header = document.createElement("div");
  header.className = "worker-job-header";
  const copy = document.createElement("div");
  copy.className = "worker-job-copy";
  const prompt = document.createElement("p");
  prompt.textContent = job.prompt;
  const meta = document.createElement("div");
  meta.className = "worker-job-meta";
  meta.innerHTML = `<span>${escapeHtml(rewardLabel(job))}</span><span>${job.width}² · ${job.steps} steps</span><span>${shortId(job.id)}</span>`;
  if (job.scope === "global") {
    const badge = document.createElement("span");
    badge.className = "global-bounty-badge";
    badge.textContent = "GLOBAL";
    meta.prepend(badge);
  }
  copy.append(prompt, meta);
  const status = document.createElement("code");
  status.textContent = presentation.label;
  status.dataset.tone = presentation.tone;
  header.append(copy, status);

  const lifecycle = document.createElement("ol");
  lifecycle.className = "work-lifecycle";
  lifecycle.setAttribute("aria-label", `Job lifecycle: ${presentation.label}`);
  for (const step of presentation.lifecycle) {
    const node = document.createElement("li");
    node.dataset.state = step.state;
    node.innerHTML = `<i aria-hidden="true"></i><span>${escapeHtml(step.label)}</span>`;
    lifecycle.append(node);
  }

  const footer = document.createElement("div");
  footer.className = "worker-job-footer";
  const detail = document.createElement("span");
  detail.textContent = presentation.detail;
  footer.append(detail);
  const transactionHash = job.settlementTransactionHash || job.chainSubmissionTransactionHash || job.chainClaimTransactionHash;
  if (transactionHash && networkConfig?.chain?.explorerUrl) {
    const link = document.createElement("a");
    link.href = `${networkConfig.chain.explorerUrl.replace(/\/$/, "")}/tx/${transactionHash}`;
    link.target = "_blank";
    link.rel = "noreferrer";
    link.textContent = "View latest transaction ↗";
    footer.append(link);
  }
  item.append(header, lifecycle, footer);
  return item;
}

function renderWorkerRewardState(status) {
  const bar = elements["worker-reward-bar"];
  const label = elements["worker-reward-label"];
  const button = elements["worker-withdraw-button"];
  if (status?.localCredits) {
    bar.dataset.state = "idle";
    label.textContent = "Credits settle directly to your balance";
    button.textContent = "Automatic";
    button.disabled = true;
    return;
  }
  if (!status) {
    bar.dataset.state = "idle";
    label.textContent = "Connect a wallet to check rewards";
    button.textContent = "Nothing to withdraw";
    button.disabled = true;
    return;
  }
  const pending = status.pending > 0n;
  bar.dataset.state = pending ? "ready" : "idle";
  label.textContent = pending ? `${Number(status.pendingLabel).toFixed(2)} ${tokenSymbol()} ready` : "No settled reward waiting";
  button.textContent = pending ? "Withdraw rewards" : "Nothing to withdraw";
  button.disabled = !pending;
}

function renderWorkerBondState(status) {
  const rail = elements["worker-bond-rail"];
  const bonding = networkConfig?.settlement?.bonding;
  rail.hidden = !bonding;
  workerBondStatus = bonding ? status : null;
  if (!bonding) return;

  const button = elements["worker-bond-button"];
  elements["worker-bond-audit-rate"].textContent = `${(Number(bonding.auditRateBps) / 100).toFixed(1)}%`;
  if (!status) {
    rail.dataset.state = "offline";
    elements["worker-bond-title"].textContent = "Connect a wallet to inspect bonded capacity";
    for (const id of ["worker-bond-staked", "worker-bond-available", "worker-bond-locked"]) elements[id].textContent = "—";
    elements["worker-bond-meter-fill"].style.width = "0%";
    elements["worker-bond-detail"].textContent = "Stake limits how much unfinished work this wallet can hold. No stake can leave during an active dispute.";
    button.textContent = "Connect wallet";
    button.disabled = true;
    button.dataset.action = "none";
    return;
  }

  const minimum = BigInt(bonding.minimumStake);
  const eligible = status.staked >= minimum && status.available >= minimum;
  const decimals = Number(networkConfig?.currency?.decimals ?? 18);
  const minimumLabel = Number(formatTokenUnits(minimum, decimals)).toFixed(2);
  rail.dataset.state = eligible ? "ready" : "locked";
  elements["worker-bond-title"].textContent = eligible
    ? "Capacity online. Fraud exposure is collateralized."
    : `Stake at least ${minimumLabel} ${tokenSymbol()} to claim work`;
  elements["worker-bond-staked"].textContent = `${Number(status.stakedLabel).toFixed(2)} ${tokenSymbol()}`;
  elements["worker-bond-available"].textContent = `${Number(status.availableLabel).toFixed(2)} ${tokenSymbol()}`;
  elements["worker-bond-locked"].textContent = `${Number(status.lockedLabel).toFixed(2)} ${tokenSymbol()}`;
  const progress = minimum > 0n ? Math.min(100, Number(status.staked * 10000n / minimum) / 100) : 0;
  elements["worker-bond-meter-fill"].style.width = `${progress}%`;

  if (status.unstakeAmount > 0n) {
    const ready = Math.floor(Date.now() / 1000) >= status.unstakeAvailableAt;
    button.dataset.action = ready ? "withdraw" : "cancel";
    button.textContent = ready ? "Withdraw stake" : "Cancel unstake";
    button.disabled = false;
    elements["worker-bond-detail"].textContent = ready
      ? "The withdrawal delay has passed. Confirm the wallet transaction to release the requested stake."
      : `Withdrawal unlocks ${new Date(status.unstakeAvailableAt * 1000).toLocaleString()}. Cancel it to restore claim capacity now.`;
    return;
  }
  if (status.available < minimum) {
    const deficit = minimum - status.available;
    button.dataset.action = "deposit";
    button.textContent = `Stake ${Number(formatTokenUnits(deficit, decimals)).toFixed(2)} ${tokenSymbol()}`;
    button.disabled = status.balance < deficit;
    elements["worker-bond-detail"].textContent = button.disabled
      ? `This wallet needs more ${tokenSymbol()} before it can meet the minimum bond.`
      : "The contract locks capacity per claimed bounty. Random audits and challenges can slash only adjudicated fraud.";
    return;
  }
  button.dataset.action = "unstake";
  button.textContent = status.available > 0n ? "Request unstake" : "Capacity locked";
  button.disabled = status.available === 0n;
  elements["worker-bond-detail"].textContent = status.locked > 0n
    ? "Some stake backs unfinished jobs. Only available stake can enter the delayed withdrawal queue."
    : "Available stake determines claim capacity. Requesting withdrawal immediately removes that amount from new-job capacity.";
}

async function manageWorkerBond() {
  const button = elements["worker-bond-button"];
  const bonding = networkConfig?.settlement?.bonding;
  if (!bonding || !workerBondStatus) return;
  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  try {
    const client = await import("./src/escrow-browser-client.js");
    const action = button.dataset.action;
    let transactionHash;
    if (action === "deposit") {
      transactionHash = await client.depositWorkerStake(
        BigInt(bonding.minimumStake) - workerBondStatus.available,
        networkConfig,
        walletProvider
      );
    } else if (action === "unstake") {
      transactionHash = await client.requestWorkerUnstake(workerBondStatus.available, networkConfig, walletProvider);
    } else if (action === "cancel") {
      transactionHash = await client.cancelWorkerUnstake(networkConfig, walletProvider);
    } else if (action === "withdraw") {
      transactionHash = await client.withdrawWorkerStake(networkConfig, walletProvider);
    } else {
      return;
    }
    setWorkerStatus("complete", "Bond updated", `Transaction confirmed · ${shortId(transactionHash)}`);
    await Promise.all([refreshBalance(), refreshQueue()]);
    renderQualificationGate();
  } catch (error) {
    setWorkerStatus("error", "Bond unchanged", getUserFacingError(error));
    await refreshBalance().catch(() => {});
  } finally {
    button.removeAttribute("aria-busy");
    renderWorkerBondState(workerBondStatus);
  }
}

async function withdrawWorkerRewards() {
  const button = elements["worker-withdraw-button"];
  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  button.textContent = "Confirm in wallet…";
  try {
    const { getEscrowWalletStatus, withdrawEscrowBalance } = await import("./src/escrow-browser-client.js");
    const status = await getEscrowWalletStatus(participantId, networkConfig, walletProvider);
    if (status.pending === 0n) throw new Error("No settled worker rewards are ready to withdraw.");
    const transactionHash = await withdrawEscrowBalance(networkConfig, walletProvider);
    setWorkerStatus("complete", "Rewards withdrawn", `${Number(status.pendingLabel).toFixed(2)} ${tokenSymbol()} · ${shortId(transactionHash)}`);
    await Promise.all([refreshBalance(), refreshWorkerHistory()]);
  } catch (error) {
    setWorkerStatus("error", "Withdrawal stopped", getUserFacingError(error));
    await refreshBalance().catch(() => {});
  } finally {
    button.removeAttribute("aria-busy");
  }
}

async function renderConsumerResult(job) {
  const item = document.createElement("li");
  item.className = "result-card";
  const top = document.createElement("div");
  top.className = "result-card-top";
  const prompt = document.createElement("p");
  prompt.textContent = job.prompt;
  const state = document.createElement("code");
  state.textContent = job.state;
  state.dataset.tone = ["expired", "rejected", "awaiting_funding"].includes(job.state) ? "warning" : "success";
  top.append(prompt, state);
  const meta = document.createElement("div");
  meta.className = "result-card-meta";
  meta.innerHTML = `<span>${escapeHtml(rewardLabel(job))}</span><span>${job.width}²</span><span>${shortId(job.id)}</span>`;
  if (job.state === "awaiting_funding") {
    const button = document.createElement("button");
    button.className = "small-button";
    button.type = "button";
    button.textContent = "Resume funding";
    button.addEventListener("click", async () => {
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.textContent = "Confirm in wallet…";
      try {
        await fundOnchainJob(job);
        setFormMessage(`Job ${shortId(job.id)} is funded and queued.`, "success");
        await Promise.all([refreshQueue(), refreshConsumerResults(), refreshBalance()]);
      } catch (error) {
        button.disabled = false;
        button.removeAttribute("aria-busy");
        button.textContent = "Resume funding";
        setFormMessage(getUserFacingError(error), "error");
      }
    });
    meta.append(button);
  }
  let expiredPresentation = null;
  if (job.state === "expired" && job.settlementMode === "onchain" && job.chainJobId) {
    try {
      const { getEscrowJobStatus } = await import("./src/escrow-browser-client.js");
      const escrow = await getEscrowJobStatus(
        job.chainJobId,
        networkConfig,
        walletProvider,
        job.settlementContractAddress || null
      );
      expiredPresentation = getExpiredEscrowPresentation(escrow.statusName);
    } catch {
      expiredPresentation = getExpiredEscrowPresentation(null, { unavailable: true });
    }
    state.textContent = expiredPresentation.displayState;
    state.dataset.tone = expiredPresentation.tone;
    const note = document.createElement("span");
    note.className = "result-state-note";
    note.textContent = expiredPresentation.note;
    meta.append(note);
  }
  if (expiredPresentation?.action === "refund") {
    const button = document.createElement("button");
    button.className = "small-button";
    button.type = "button";
    button.textContent = "Refund escrow";
    button.addEventListener("click", async () => {
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.textContent = "Confirm in wallet…";
      try {
        const { refundExpiredEscrow } = await import("./src/escrow-browser-client.js");
        await refundExpiredEscrow(
          job.chainJobId,
          networkConfig,
          walletProvider,
          job.settlementContractAddress || null
        );
        setFormMessage(`Refund for ${shortId(job.id)} is available to withdraw.`, "success");
        await Promise.all([refreshBalance(), refreshConsumerResults()]);
      } catch (error) {
        button.disabled = false;
        button.removeAttribute("aria-busy");
        button.textContent = "Refund escrow";
        setFormMessage(getUserFacingError(error), "error");
      }
    });
    meta.append(button);
  }
  if (job.state === "accepted") {
    const button = document.createElement("button");
    button.className = "small-button";
    button.type = "button";
    button.textContent = "View result";
    button.disabled = !getConsumerToken(job.id);
    button.addEventListener("click", () => loadConsumerResult(job, item, button));
    meta.append(button);
  }
  item.append(top, meta);
  return item;
}

async function loadConsumerResult(job, item, button) {
  const consumerToken = getConsumerToken(job.id);
  if (!consumerToken) return;
  button.disabled = true;
  try {
    const response = await fetch(`/api/jobs/${encodeURIComponent(job.id)}/result`, {
      headers: { "x-consumer-token": consumerToken }
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error?.message || `Result returned HTTP ${response.status}.`);
    }
    if (resultObjectUrls.has(job.id)) URL.revokeObjectURL(resultObjectUrls.get(job.id));
    const url = URL.createObjectURL(await response.blob());
    resultObjectUrls.set(job.id, url);
    let image = item.querySelector("img");
    if (!image) {
      image = document.createElement("img");
      image.alt = `Accepted result for ${job.prompt}`;
      item.append(image);
    }
    image.src = url;
    button.textContent = "Result loaded";
  } catch (error) {
    button.disabled = false;
    setFormMessage(getUserFacingError(error), "error");
  }
}

function renderQualificationGate() {
  const eligibility = contributionEligibility();
  const walletReady = !networkConfig?.authentication?.walletSessionRequired || Boolean(walletSession);
  const runtimeReady = typeof globalThis.SHAREMYAI_FLUX_ADAPTER?.render === "function";
  const canonicalSubmitted = ["pending", "qualified"].includes(contributorQualification?.state);
  const quorumReady = contributorQualification?.state === "qualified"
    && Date.parse(contributorQualification.expiresAt) > Date.now();
  const bondingRequired = Boolean(networkConfig?.settlement?.bonding);
  const bondReady = !bondingRequired || bondedCapacityReady();
  const check = (label, state) => `<li data-state="${state}"><i aria-hidden="true"></i><span>${label}</span></li>`;
  elements["queue-gate"].dataset.state = eligibility.allowed ? "ready" : eligibility.tone;
  elements["queue-gate"].innerHTML = `
    <span class="gate-node" aria-hidden="true"></span>
    <div class="claim-gate-copy">
      <strong>${eligibility.title}</strong>
      <p>${eligibility.detail}</p>
      <ol class="claim-checks" aria-label="Contributor eligibility checkpoints">
        ${check("Wallet session", walletReady ? "complete" : "blocked")}
        ${check("Model runtime", runtimeReady ? "complete" : "blocked")}
        ${check("Canonical output", canonicalSubmitted ? "complete" : "blocked")}
        ${check("Verifier quorum", quorumReady ? "complete" : contributorQualification?.state === "pending" ? "active" : "blocked")}
        ${check("Bonded capacity", bondReady ? "complete" : "blocked")}
      </ol>
      ${eligibility.allowed ? "" : `<a class="gate-action" href="${bondingRequired && !bondReady ? "#worker-bond-rail" : "#device"}">${bondingRequired && !bondReady ? "Open stake controls" : "Open device setup"} →</a>`}
    </div>`;
  renderAutoWorkerControl();
}

function canContribute() {
  return contributionEligibility().allowed;
}

function contributionEligibility(job = null) {
  const bonding = networkConfig?.settlement?.bonding;
  const requiredExposure = bonding && job?.rewardWei
    ? (BigInt(job.rewardWei) * BigInt(bonding.stakeMultiplierBps) + 9_999n) / 10_000n
    : bonding ? BigInt(bonding.minimumStake) : 0n;
  return getContributionEligibility({
    jobConsumerId: job?.consumerId,
    participantId,
    activeWorkerJobId,
    walletRequired: networkConfig?.authentication?.walletSessionRequired === true,
    walletSessionReady: Boolean(walletSession),
    hasDeviceReceipt: Boolean(receipt),
    runtimeReady: typeof globalThis.SHAREMYAI_FLUX_ADAPTER?.render === "function",
    qualificationState: contributorQualification?.state,
    qualificationExpiresAt: contributorQualification?.expiresAt,
    bondingRequired: Boolean(bonding),
    bondEligible: !bonding || Boolean(workerBondStatus
      && workerBondStatus.staked >= BigInt(bonding.minimumStake)
      && workerBondStatus.available >= requiredExposure)
  });
}

function bondedCapacityReady() {
  const bonding = networkConfig?.settlement?.bonding;
  return !bonding || Boolean(workerBondStatus
    && workerBondStatus.staked >= BigInt(bonding.minimumStake)
    && workerBondStatus.available >= BigInt(bonding.minimumStake));
}

function formatTokenUnits(value, decimals) {
  const divisor = 10n ** BigInt(decimals);
  const whole = value / divisor;
  const fraction = (value % divisor).toString().padStart(decimals, "0").slice(0, 6).replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

function setFormMessage(message, tone = "neutral") {
  elements["job-form-message"].textContent = message;
  elements["job-form-message"].dataset.tone = tone;
}

async function apiRequest(path, options = {}) {
  const response = await fetch(path, {
    ...options,
    headers: {
      "content-type": "application/json",
      ...(walletSession?.sessionToken ? { "x-wallet-session": walletSession.sessionToken } : {}),
      ...(options.headers || {})
    }
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error?.message || `Coordinator returned HTTP ${response.status}.`);
    error.code = payload.error?.code || "http_error";
    error.status = response.status;
    throw error;
  }
  return payload;
}

function getParticipantId() {
  const key = "sharemyai.participant.v1";
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const id = `participant_${crypto.randomUUID()}`;
  localStorage.setItem(key, id);
  return id;
}

function saveConsumerToken(jobId, token) {
  if (!token) return;
  const tokens = readConsumerTokens();
  tokens[jobId] = token;
  localStorage.setItem("sharemyai.consumer-tokens.v1", JSON.stringify(tokens));
}

function getConsumerToken(jobId) {
  return readConsumerTokens()[jobId] || null;
}

function readConsumerTokens() {
  try { return JSON.parse(localStorage.getItem("sharemyai.consumer-tokens.v1") || "{}"); }
  catch { return {}; }
}

function saveQualificationGrant(accountId, qualification) {
  const grants = readQualificationGrants();
  if (qualification) grants[accountId] = qualification;
  else delete grants[accountId];
  localStorage.setItem("sharemyai.qualification-grants.v1", JSON.stringify(grants));
}

function readQualificationGrants() {
  try { return JSON.parse(localStorage.getItem("sharemyai.qualification-grants.v1") || "{}"); }
  catch { return {}; }
}

async function createWalletSession(wallet) {
  if (!walletProvider?.request) throw new Error("Select a wallet before authenticating.");
  const challenge = await apiRequest("/api/auth/challenge", {
    method: "POST",
    body: JSON.stringify({ wallet })
  });
  const signature = await walletProvider.request({
    method: "eth_signTypedData_v4",
    params: [wallet, JSON.stringify(challenge.typedData)]
  });
  walletSession = await apiRequest("/api/auth/session", {
    method: "POST",
    body: JSON.stringify({ nonce: challenge.nonce, signature })
  });
  saveWalletSession(wallet, walletSession);
}

async function restoreWalletSession(wallet) {
  walletSession = null;
  const saved = readWalletSessions()[wallet];
  if (!saved?.sessionToken || !saved.expiresAt || Date.parse(saved.expiresAt) <= Date.now()) {
    saveWalletSession(wallet, null);
    return;
  }
  try {
    const response = await fetch("/api/auth/session", { headers: { "x-wallet-session": saved.sessionToken } });
    if (!response.ok) {
      saveWalletSession(wallet, null);
      return;
    }
    const status = await response.json();
    if (status.wallet.toLowerCase() !== wallet.toLowerCase()) {
      saveWalletSession(wallet, null);
      return;
    }
    walletSession = { ...saved, wallet: status.wallet, expiresAt: status.expiresAt };
    saveWalletSession(wallet, walletSession);
  } catch {
    walletSession = null;
  }
}

function saveWalletSession(wallet, session) {
  const sessions = readWalletSessions();
  if (session) sessions[wallet] = session;
  else delete sessions[wallet];
  localStorage.setItem("sharemyai.wallet-sessions.v1", JSON.stringify(sessions));
}

function readWalletSessions() {
  try { return JSON.parse(localStorage.getItem("sharemyai.wallet-sessions.v1") || "{}"); }
  catch { return {}; }
}

function saveFundingTransaction(jobId, transactionHash) {
  const transactions = readFundingTransactions();
  if (transactionHash) transactions[jobId] = transactionHash;
  else delete transactions[jobId];
  localStorage.setItem("sharemyai.funding-transactions.v1", JSON.stringify(transactions));
}

function getFundingTransaction(jobId) {
  return readFundingTransactions()[jobId] || null;
}

function readFundingTransactions() {
  try { return JSON.parse(localStorage.getItem("sharemyai.funding-transactions.v1") || "{}"); }
  catch { return {}; }
}

async function restoreQualificationGrant() {
  contributorQualification = null;
  const saved = readQualificationGrants()[participantId];
  if (!saved?.id || !saved?.accessToken || !saved?.grantToken) {
    renderQualificationGate();
    return;
  }
  if (!saved.expiresAt || Date.parse(saved.expiresAt) <= Date.now()) {
    saveQualificationGrant(participantId, null);
    renderQualificationGate();
    return;
  }
  try {
    const response = await fetch(`/api/qualifications/${encodeURIComponent(saved.id)}`, {
      headers: { "x-qualification-access-token": saved.accessToken }
    });
    const status = await response.json().catch(() => ({}));
    if (!response.ok) {
      saveQualificationGrant(participantId, null);
      renderQualificationGate();
      return;
    }
    if (!["pending", "qualified"].includes(status.state) || Date.parse(status.expiresAt) <= Date.now()) {
      saveQualificationGrant(participantId, null);
      renderQualificationGate();
      return;
    }
    contributorQualification = {
      ...saved,
      state: status.state,
      expiresAt: status.expiresAt
    };
    saveQualificationGrant(participantId, contributorQualification);
  } catch {
    // A transient coordinator outage must not turn a cached bearer grant into
    // an apparently active credential. It will be restored on the next sync.
    contributorQualification = null;
  }
  renderQualificationGate();
}

function shortId(value) {
  return value.length > 16 ? `${value.slice(0, 8)}…${value.slice(-5)}` : value;
}
